/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { jsPDF } from 'jspdf';
import { SafetyIncident, SafetyTalk, DC3Record } from '../types.ts';
import { CORPORATE_LOGO_BASE64 } from './corporateLogoBase64.ts';

/**
 * Generates an official, highly detailed industrial safety report (STPS / ISO 45001) in PDF format
 * for incident investigation, root cause analysis, corrective actions, and photographic evidence.
 */
export async function generateSafetyIncidentPDF(incident: SafetyIncident): Promise<void> {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'letter' // 216mm x 279mm
  });

  const pageWidth = 216;
  const pageHeight = 279;
  const marginX = 14;
  const contentWidth = pageWidth - (marginX * 2); // 188mm

  // Classification colors
  let badgeBg: [number, number, number] = [220, 38, 38]; // Default Red
  let badgeLabel = 'ACCIDENTE CON TIEMPO PERDIDO (LTI)';

  if (incident.classification === 'safe') {
    badgeBg = [16, 185, 129];
    badgeLabel = 'DÍA SEGURO / SIN NOVEDAD';
  } else if (incident.classification === 'near_miss') {
    badgeBg = [217, 119, 6];
    badgeLabel = 'CASI INCIDENTE / CONDICIÓN INSEGURA';
  } else if (incident.classification === 'first_aid') {
    badgeBg = [37, 99, 235];
    badgeLabel = 'PRIMEROS AUXILIOS / ATENCIÓN MENOR';
  } else if (incident.classification === 'medical_treatment') {
    badgeBg = [234, 88, 12];
    badgeLabel = 'ACCIDENTE SIN TIEMPO PERDIDO';
  } else if (incident.classification === 'environmental') {
    badgeBg = [147, 51, 234];
    badgeLabel = 'INCIDENTE AMBIENTAL / DERRAME';
  }

  // --- DRAW HEADER ---
  // Top Accent bar
  doc.setFillColor(188, 17, 0); // Brand Red #BC1100
  doc.rect(0, 0, pageWidth, 3.5, 'F');

  // Main Header Box (Dark Slate)
  doc.setFillColor(15, 23, 42);
  doc.rect(0, 3.5, pageWidth, 32, 'F');

  // Corporate Logo
  try {
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(marginX, 6.5, 26, 26, 2, 2, 'F');
    doc.addImage(CORPORATE_LOGO_BASE64, 'JPEG', marginX + 1, 7.5, 24, 24);
  } catch {
    // Fallback logo
    doc.setFillColor(188, 17, 0);
    doc.roundedRect(marginX, 7.5, 22, 23, 2, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('SNF', marginX + 4, 20);
  }

  // Company Name
  const textStartX = marginX + 29;
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text('SERVICIOS NUEVOS DE FILTRACIÓN', textStartX, 13);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(203, 213, 225);
  doc.text('DEPARTAMENTO DE SEGURIDAD INDUSTRIAL, SALUD OCUPACIONAL Y MEDIO AMBIENTE (HSEQ)', textStartX, 17.5);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(248, 113, 113); // Light red
  doc.text('REPORTE OFICIAL DE INVESTIGACIÓN DE INCIDENTES Y ACCIDENTES (NOM-019/021-STPS)', textStartX, 23.5);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.setTextColor(148, 163, 184);
  doc.text(`Sede Operativa: ${incident.costCenterName || incident.costCenterCode || 'General'}  |  Norma ISO 45001:2018`, textStartX, 28);

  // Right Header Badge (Folio Box)
  doc.setFillColor(30, 41, 59);
  doc.setDrawColor(188, 17, 0);
  doc.setLineWidth(0.5);
  doc.roundedRect(pageWidth - marginX - 48, 6.5, 48, 26, 2, 2, 'FD');

  doc.setTextColor(148, 163, 184);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6);
  doc.text('FOLIO DE INVESTIGACIÓN', pageWidth - marginX - 45, 11);

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text(incident.id || 'INC-2026-08', pageWidth - marginX - 45, 17);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.setTextColor(226, 232, 240);
  doc.text(`Fecha: ${incident.date}`, pageWidth - marginX - 45, 22);
  doc.text(`Hora: ${incident.time || 'N/R'} hrs`, pageWidth - marginX - 45, 26);

  // --- CLASSIFICATION BANNER ---
  let currentY = 38.5;
  doc.setFillColor(...badgeBg);
  doc.roundedRect(marginX, currentY, contentWidth, 7, 1.5, 1.5, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.text(`CLASIFICACIÓN: ${badgeLabel}  —  SEVERIDAD: ${(incident.severity || 'MEDIA').toUpperCase()}`, marginX + 4, currentY + 4.8);

  if (incident.lostDays > 0) {
    doc.setFontSize(7.5);
    doc.text(`DÍAS DE INCAPACIDAD: ${incident.lostDays} DÍAS`, pageWidth - marginX - 45, currentY + 4.8);
  }

  currentY += 10;

  // Helper function to draw section titles
  function drawSectionTitle(title: string, yPos: number): number {
    doc.setFillColor(241, 245, 249); // Slate-100
    doc.setDrawColor(203, 213, 225); // Slate-300
    doc.setLineWidth(0.3);
    doc.roundedRect(marginX, yPos, contentWidth, 5.5, 1, 1, 'FD');

    // Accent line
    doc.setFillColor(188, 17, 0);
    doc.rect(marginX, yPos, 2.5, 5.5, 'F');

    doc.setTextColor(15, 23, 42);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.text(title.toUpperCase(), marginX + 5, yPos + 3.8);

    return yPos + 7.5;
  }

  // --- 1. DATOS GENERALES Y UBICACIÓN ---
  currentY = drawSectionTitle('1. Datos Generales de la Sede y Localización del Evento', currentY);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);

  const col1 = marginX;
  const col2 = marginX + 64;
  const col3 = marginX + 128;

  // Row 1
  doc.setTextColor(100, 116, 139);
  doc.text('Centro de Costos / Sede:', col1, currentY);
  doc.setTextColor(15, 23, 42);
  doc.setFont('helvetica', 'bold');
  doc.text(incident.costCenterName || incident.costCenterCode || 'Salamanca Bajío', col1, currentY + 3.8);

  doc.setTextColor(100, 116, 139);
  doc.setFont('helvetica', 'normal');
  doc.text('Área Específica en Planta:', col2, currentY);
  doc.setTextColor(15, 23, 42);
  doc.setFont('helvetica', 'bold');
  doc.text(incident.area || 'Línea de Hornos Térmicos', col2, currentY + 3.8);

  doc.setTextColor(100, 116, 139);
  doc.setFont('helvetica', 'normal');
  doc.text('Turno / Horario:', col3, currentY);
  doc.setTextColor(15, 23, 42);
  doc.setFont('helvetica', 'bold');
  doc.text(incident.shift || 'Turno 1 (Matutino)', col3, currentY + 3.8);

  currentY += 9;

  // --- 2. PERSONAL INVOLUCRADO Y SUPERVISIÓN ---
  currentY = drawSectionTitle('2. Personal Involucrado y Datos del Afectado', currentY);

  doc.setTextColor(100, 116, 139);
  doc.setFont('helvetica', 'normal');
  doc.text('Colaborador Afectado:', col1, currentY);
  doc.setTextColor(15, 23, 42);
  doc.setFont('helvetica', 'bold');
  doc.text(incident.affectedPersonName || 'No Aplica / Sin Lesión Personal', col1, currentY + 3.8);

  doc.setTextColor(100, 116, 139);
  doc.setFont('helvetica', 'normal');
  doc.text('Puesto / Especialidad:', col2, currentY);
  doc.setTextColor(15, 23, 42);
  doc.setFont('helvetica', 'bold');
  doc.text(incident.affectedPersonRole || 'Operador General', col2, currentY + 3.8);

  doc.setTextColor(100, 116, 139);
  doc.setFont('helvetica', 'normal');
  doc.text('No. Nómina / ID:', col3, currentY);
  doc.setTextColor(15, 23, 42);
  doc.setFont('helvetica', 'bold');
  doc.text(incident.affectedPersonEmployeeId || 'ISN-OP-000', col3, currentY + 3.8);

  currentY += 7.5;

  doc.setTextColor(100, 116, 139);
  doc.setFont('helvetica', 'normal');
  doc.text('Supervisor de Turno:', col1, currentY);
  doc.setTextColor(15, 23, 42);
  doc.setFont('helvetica', 'bold');
  doc.text(incident.supervisorName || 'Ing. Supervisor HSEQ', col1, currentY + 3.8);

  doc.setTextColor(100, 116, 139);
  doc.setFont('helvetica', 'normal');
  doc.text('EPP en Uso al Momento:', col2, currentY);
  doc.setTextColor(15, 23, 42);
  doc.setFont('helvetica', 'bold');
  const eppLines = doc.splitTextToSize(incident.ppeUsed || 'Careta facial, guantes de nitrilo, botas de seguridad con casquillo', contentWidth - 66);
  doc.text(eppLines, col2, currentY + 3.8);

  currentY += Math.max(7.5, (eppLines.length * 3.5) + 3);

  // --- 3. LESIÓN Y ATENCIÓN MÉDICA ---
  if (incident.classification !== 'safe') {
    currentY = drawSectionTitle('3. Evaluación Médica y Lesión Corporal', currentY);

    doc.setTextColor(100, 116, 139);
    doc.setFont('helvetica', 'normal');
    doc.text('Parte del Cuerpo Afectada:', col1, currentY);
    doc.setTextColor(15, 23, 42);
    doc.setFont('helvetica', 'bold');
    doc.text(incident.affectedBodyPart || 'Ninguna / Sin lesión física', col1, currentY + 3.8);

    doc.setTextColor(100, 116, 139);
    doc.setFont('helvetica', 'normal');
    doc.text('Naturaleza de la Lesión:', col2, currentY);
    doc.setTextColor(15, 23, 42);
    doc.setFont('helvetica', 'bold');
    doc.text(incident.injuryType || 'Ninguna', col2, currentY + 3.8);

    doc.setTextColor(100, 116, 139);
    doc.setFont('helvetica', 'normal');
    doc.text('Tipo de Atención Médica:', col3, currentY);
    doc.setTextColor(15, 23, 42);
    doc.setFont('helvetica', 'bold');
    doc.text(incident.medicalAttention || 'Botiquín Interno', col3, currentY + 3.8);

    currentY += 8.5;
  }

  // --- 4. DESCRIPCIÓN CIRCUNSTANCIADA DE LOS HECHOS ---
  currentY = drawSectionTitle('4. Descripción Circunstanciada de los Hechos (Qué y Cómo Sucedió)', currentY);

  if (incident.title) {
    doc.setTextColor(188, 17, 0);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text(`Asunto: ${incident.title}`, marginX, currentY);
    currentY += 4.5;
  }

  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.2);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.2);
  doc.setTextColor(30, 41, 59);

  const descText = incident.description || 'Sin descripción complementaria registrada.';
  const descLines = doc.splitTextToSize(descText, contentWidth - 6);
  const descBoxHeight = Math.max(12, (descLines.length * 3.6) + 4);

  doc.roundedRect(marginX, currentY, contentWidth, descBoxHeight, 1, 1, 'FD');
  doc.text(descLines, marginX + 3, currentY + 4);

  currentY += descBoxHeight + 4;

  // --- 5. ANÁLISIS DE CAUSA RAÍZ (STPS / ISHIKAWA) ---
  currentY = drawSectionTitle('5. Análisis Causal y Causa Raíz (Estándares STPS / 5 Porqués)', currentY);

  const boxW = (contentWidth - 4) / 3;

  // Acto Inseguro
  doc.setFillColor(254, 242, 242); // Red-50
  doc.setDrawColor(254, 202, 202);
  doc.roundedRect(marginX, currentY, boxW, 20, 1, 1, 'FD');
  doc.setTextColor(153, 27, 27);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.8);
  doc.text('A) ACTO INSEGURO DETECTADO', marginX + 2, currentY + 3.5);
  doc.setTextColor(69, 10, 10);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  const actLines = doc.splitTextToSize(incident.unsafeAct || 'Ninguno detectado.', boxW - 4);
  doc.text(actLines, marginX + 2, currentY + 7);

  // Condición Insegura
  doc.setFillColor(255, 251, 235); // Amber-50
  doc.setDrawColor(254, 243, 199);
  doc.roundedRect(marginX + boxW + 2, currentY, boxW, 20, 1, 1, 'FD');
  doc.setTextColor(146, 64, 14);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.8);
  doc.text('B) CONDICIÓN INSEGURA DETECTADA', marginX + boxW + 4, currentY + 3.5);
  doc.setTextColor(120, 53, 15);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  const condLines = doc.splitTextToSize(incident.unsafeCondition || 'Ninguna detectada.', boxW - 4);
  doc.text(condLines, marginX + boxW + 4, currentY + 7);

  // Causa Raíz
  doc.setFillColor(240, 253, 244); // Green-50
  doc.setDrawColor(187, 247, 208);
  doc.roundedRect(marginX + (boxW * 2) + 4, currentY, boxW, 20, 1, 1, 'FD');
  doc.setTextColor(22, 101, 52);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.8);
  doc.text('C) CAUSA RAÍZ CONCLUSIVA', marginX + (boxW * 2) + 6, currentY + 3.5);
  doc.setTextColor(20, 83, 45);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  const rootLines = doc.splitTextToSize(incident.rootCause || 'En análisis por comisión mixta HSEQ.', boxW - 4);
  doc.text(rootLines, marginX + (boxW * 2) + 6, currentY + 7);

  currentY += 23;

  // --- 6. PLAN DE ACCIONES CORRECTIVAS Y PREVENTIVAS (CAPA) ---
  currentY = drawSectionTitle('6. Plan de Acciones Correctivas y Preventivas (CAPA)', currentY);

  const capaBoxW = (contentWidth - 2) / 2;

  // Immediate Action
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(203, 213, 225);
  doc.roundedRect(marginX, currentY, capaBoxW, 18, 1, 1, 'FD');
  doc.setTextColor(30, 41, 59);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.8);
  doc.text('ACCION INMEDIATA DE CONTENCIÓN:', marginX + 2.5, currentY + 3.5);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  const immLines = doc.splitTextToSize(incident.immediateAction || 'Contención inmediata y aseguramiento del área.', capaBoxW - 5);
  doc.text(immLines, marginX + 2.5, currentY + 7);

  // Preventive Action
  doc.roundedRect(marginX + capaBoxW + 2, currentY, capaBoxW, 18, 1, 1, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.8);
  doc.text('ACCIÓN CORRECTIVA / PREVENTIVA:', marginX + capaBoxW + 4.5, currentY + 3.5);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  const prevLines = doc.splitTextToSize(incident.preventiveAction || 'Actualización de procedimiento e instrucción operativa.', capaBoxW - 5);
  doc.text(prevLines, marginX + capaBoxW + 4.5, currentY + 7);

  currentY += 19.5;

  // Owner, Deadline, Status Row
  doc.setTextColor(71, 85, 105);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.8);
  doc.text(`Responsable de Ejecución: ${incident.actionOwner || 'Supervisor HSEQ / Mantenimiento'}`, marginX, currentY);
  doc.text(`Fecha Compromiso: ${incident.actionDeadline || 'Inmediato'}`, marginX + 80, currentY);
  doc.text(`Estatus CAPA: ${(incident.actionStatus || 'Implementada').toUpperCase()}`, marginX + 140, currentY);

  currentY += 6;

  // --- 7. EVIDENCIA FOTOGRÁFICA (Up to 5 photos) ---
  const validPhotos = (incident.photos || []).filter(p => p && p.dataUrl);

  if (validPhotos.length > 0) {
    // Check if we need page 2 for photos or if photos fit
    if (currentY > 195) {
      doc.addPage();
      currentY = 15;
      // Header for Page 2
      doc.setFillColor(15, 23, 42);
      doc.rect(0, 0, pageWidth, 10, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.5);
      doc.text(`REPORTE DE INCIDENTE ${incident.id}  —  EVIDENCIA FOTOGRÁFICA (ANEXO)`, marginX, 6.5);
      currentY = 16;
    }

    currentY = drawSectionTitle(`7. Evidencia Fotográfica del Hallazgo (${validPhotos.length} / 5 Fotografías Registradas)`, currentY);

    const maxCols = Math.min(validPhotos.length, 3);
    const photoWidth = (contentWidth - ((maxCols - 1) * 3)) / maxCols;
    const photoHeight = 36;

    let photoIndex = 0;
    while (photoIndex < validPhotos.length) {
      const currentBatch = validPhotos.slice(photoIndex, photoIndex + maxCols);
      const batchStartX = marginX;

      currentBatch.forEach((photo, idx) => {
        const pX = batchStartX + (idx * (photoWidth + 3));
        const pY = currentY;

        try {
          // Draw white frame box
          doc.setFillColor(255, 255, 255);
          doc.setDrawColor(203, 213, 225);
          doc.setLineWidth(0.4);
          doc.roundedRect(pX, pY, photoWidth, photoHeight + 7, 1, 1, 'FD');

          // Add image
          doc.addImage(photo.dataUrl, 'JPEG', pX + 1, pY + 1, photoWidth - 2, photoHeight - 2);

          // Caption box
          doc.setFillColor(241, 245, 249);
          doc.rect(pX + 0.5, pY + photoHeight - 0.5, photoWidth - 1, 7, 'F');
          doc.setTextColor(30, 41, 59);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(5.8);
          const captionText = photo.caption ? `Foto ${photoIndex + idx + 1}: ${photo.caption}` : `Foto ${photoIndex + idx + 1}: Evidencia de lugar de los hechos`;
          const captionLines = doc.splitTextToSize(captionText, photoWidth - 2);
          doc.text(captionLines, pX + 2, pY + photoHeight + 3);
        } catch (e) {
          console.error('Error rendering incident photo:', e);
          doc.setFillColor(241, 245, 249);
          doc.roundedRect(pX, pY, photoWidth, photoHeight, 1, 1, 'FD');
          doc.setTextColor(148, 163, 184);
          doc.setFontSize(6);
          doc.text('Foto adjunta no disponible', pX + 4, pY + (photoHeight / 2));
        }
      });

      currentY += photoHeight + 10;
      photoIndex += maxCols;

      if (photoIndex < validPhotos.length && currentY > 220) {
        doc.addPage();
        currentY = 15;
      }
    }
  }

  // --- 8. FIRMAS DE VALIDACIÓN Y DICTAMEN OFICIAL ---
  if (currentY > 230) {
    doc.addPage();
    currentY = 20;
  }

  currentY = drawSectionTitle('8. Firmas de Validación y Dictamen Oficial de Seguridad', currentY);

  const sigWidth = (contentWidth - 8) / 3;
  const sigY = currentY + 14;

  // Signature 1: Supervisor HSEQ
  doc.setDrawColor(100, 116, 139);
  doc.setLineWidth(0.4);
  doc.line(marginX, sigY, marginX + sigWidth, sigY);
  doc.setTextColor(15, 23, 42);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.8);
  doc.text('INVESTIGADO Y REPORTADO POR', marginX, sigY + 3.5);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6);
  doc.setTextColor(71, 85, 105);
  doc.text(incident.supervisorName || 'Supervisor de Seguridad HSEQ', marginX, sigY + 7);
  doc.text('Comisión de Seguridad e Higiene', marginX, sigY + 10);

  // Signature 2: Gerente de Planta
  const sig2X = marginX + sigWidth + 4;
  doc.line(sig2X, sigY, sig2X + sigWidth, sigY);
  doc.setTextColor(15, 23, 42);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.8);
  doc.text('REVISADO Y APROBADO POR', sig2X, sigY + 3.5);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6);
  doc.setTextColor(71, 85, 105);
  doc.text('Gerente de Planta / Operaciones', sig2X, sigY + 7);
  doc.text('Servicios Nuevos de Filtración', sig2X, sigY + 10);

  // Signature 3: Trabajador Involucrado
  const sig3X = marginX + (sigWidth * 2) + 8;
  doc.line(sig3X, sigY, sig3X + sigWidth, sigY);
  doc.setTextColor(15, 23, 42);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.8);
  doc.text('ENTERADO Y CONFORME', sig3X, sigY + 3.5);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6);
  doc.setTextColor(71, 85, 105);
  doc.text(incident.affectedPersonName || 'Colaborador / Operador', sig3X, sigY + 7);
  doc.text(`ID: ${incident.affectedPersonEmployeeId || 'ISN-OP'}`, sig3X, sigY + 10);

  // --- FOOTER ON ALL PAGES ---
  const totalPages = (doc as any).internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);

    // Bottom accent line
    doc.setFillColor(188, 17, 0);
    doc.rect(0, pageHeight - 3, pageWidth, 3, 'F');

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6);
    doc.setTextColor(148, 163, 184);
    doc.text('Documento oficial confidencial de investigación de incidentes HSEQ - Grupo ISN / SNF. Cumplimiento con NOM-019-STPS / NOM-021-STPS / ISO 45001.', marginX, pageHeight - 5);
    doc.text(`Página ${i} de ${totalPages}`, pageWidth - marginX - 18, pageHeight - 5);
  }

  // Save the PDF
  const sanitizedId = (incident.id || 'INC-SEGURIDAD').replace(/[^a-zA-Z0-9-_]/g, '_');
  const filename = `Reporte_Seguridad_${sanitizedId}_${incident.date}.pdf`;
  doc.save(filename);
}

export interface MonthlySafetyCrossReportData {
  costCenterName: string;
  costCenterCode: string;
  year: number;
  monthIndex: number;
  monthName: string;
  incidents: SafetyIncident[];
  calendarDays: { day: number; incident?: SafetyIncident; isFuture: boolean }[];
  metrics: {
    daysWithoutIncident: number;
    safeDaysCount: number;
    elapsedDaysInMonth: number;
    pendingDaysCount: number;
    nearMissCount: number;
    firstAidCount: number;
    medTreatmentCount: number;
    lostTimeCount: number;
    envCount: number;
    totalMonth: number;
    totalLostDays: number;
  };
}

/**
 * Generates an official, comprehensive Monthly Industrial Safety Cross Report in PDF format
 * strictly compliant with Mexican STPS regulations (NOM-019-STPS-2011, NOM-021-STPS-1994) and ISO 45001:2018.
 */
export async function generateMonthlySafetyCrossPDF(data: MonthlySafetyCrossReportData): Promise<void> {
  const {
    costCenterName,
    costCenterCode,
    year,
    monthIndex,
    monthName,
    incidents,
    calendarDays,
    metrics
  } = data;

  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'letter' // 216mm x 279mm
  });

  const pageWidth = 216;
  const pageHeight = 279;
  const marginX = 14;
  const contentWidth = pageWidth - (marginX * 2); // 188mm

  const monthNumber = String(monthIndex + 1).padStart(2, '0');
  const folioCode = `RPT-CS-${year}-${monthNumber}-${(costCenterCode || 'SEDE').replace('CC-', '')}`;
  const currentDateFormatted = new Date().toLocaleDateString('es-MX', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });

  // Estimated Man-Hours for normative calculations (e.g. 150 workers * 8 hrs * elapsed days)
  const estimatedHHT = Math.max(1, (metrics.elapsedDaysInMonth || 1) * 120 * 8);
  const frequencyIndex = ((metrics.lostTimeCount * 1000000) / estimatedHHT).toFixed(2);
  const severityIndex = ((metrics.totalLostDays * 1000000) / estimatedHHT).toFixed(2);

  // --- HELPER: Draw Section Title ---
  function drawSectionTitle(title: string, yPos: number): number {
    doc.setFillColor(241, 245, 249); // Slate-100
    doc.setDrawColor(203, 213, 225); // Slate-300
    doc.setLineWidth(0.3);
    doc.roundedRect(marginX, yPos, contentWidth, 5.2, 1, 1, 'FD');

    // Accent line
    doc.setFillColor(188, 17, 0);
    doc.rect(marginX, yPos, 2.5, 5.2, 'F');

    doc.setTextColor(15, 23, 42);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.text(title.toUpperCase(), marginX + 5, yPos + 3.6);

    return yPos + 7;
  }

  // ==========================================
  // PAGE 1: HEADER, KPIS, AND SAFETY CROSS MATRIX
  // ==========================================

  // 1. Top Accent bar
  doc.setFillColor(188, 17, 0); // Brand Red
  doc.rect(0, 0, pageWidth, 3.5, 'F');

  // Main Header Box (Dark Slate Navy)
  doc.setFillColor(15, 23, 42);
  doc.rect(0, 3.5, pageWidth, 32, 'F');

  // Corporate Logo Box
  try {
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(marginX, 6.5, 26, 26, 2, 2, 'F');
    doc.addImage(CORPORATE_LOGO_BASE64, 'JPEG', marginX + 1, 7.5, 24, 24);
  } catch {
    doc.setFillColor(188, 17, 0);
    doc.roundedRect(marginX, 7.5, 22, 23, 2, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('SNF', marginX + 4, 20);
  }

  // Company Name & Department
  const textStartX = marginX + 29;
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('SERVICIOS NUEVOS DE FILTRACIÓN S.A. DE C.V.', textStartX, 12);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.8);
  doc.setTextColor(203, 213, 225);
  doc.text('DEPARTAMENTO DE SEGURIDAD INDUSTRIAL, SALUD OCUPACIONAL Y MEDIO AMBIENTE (HSEQ)', textStartX, 16.5);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(52, 211, 153); // Emerald-400
  doc.text('REPORTE MENSUAL DE CRUZ DE SEGURIDAD INDUSTRIAL Y ESTADÍSTICA DE SINIESTRALIDAD', textStartX, 22.5);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.setTextColor(148, 163, 184);
  doc.text(`Sede / C.C.: ${costCenterName} (${costCenterCode || 'SEDE'})  |  Periodo: ${monthName} ${year}  |  NOM-019-STPS / ISO 45001:2018`, textStartX, 27.5);

  // Folio Box (Top Right)
  doc.setFillColor(30, 41, 59);
  doc.setDrawColor(52, 211, 153);
  doc.setLineWidth(0.4);
  doc.roundedRect(pageWidth - marginX - 48, 6.5, 48, 26, 2, 2, 'FD');

  doc.setTextColor(148, 163, 184);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(5.8);
  doc.text('FOLIO DE CONTROL MENSUAL', pageWidth - marginX - 45, 10.5);

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.text(folioCode, pageWidth - marginX - 45, 16);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.2);
  doc.setTextColor(226, 232, 240);
  doc.text(`Emisión: ${currentDateFormatted}`, pageWidth - marginX - 45, 21);
  doc.text(`Días Transcurridos: ${metrics.elapsedDaysInMonth} / ${calendarDays.length}`, pageWidth - marginX - 45, 25.5);

  let currentY = 38.5;

  // --- 1. RESUMEN DE INDICADORES DE SINIESTRALIDAD (KPIs) ---
  currentY = drawSectionTitle('1. Tablero Ejecutivo de Indicadores Clave de Desempeño (KPIs HSEQ)', currentY);

  const kpiBoxWidth = (contentWidth - 10) / 6;
  const kpiHeight = 15;

  const kpiData = [
    {
      title: 'DÍAS SIN LTI',
      value: `${metrics.daysWithoutIncident}`,
      sub: 'Cero incapacitantes',
      bg: [236, 253, 245],
      border: [16, 185, 129],
      text: [4, 120, 87]
    },
    {
      title: 'DÍAS SEGUROS',
      value: `${metrics.safeDaysCount} / ${metrics.elapsedDaysInMonth}`,
      sub: `Sin novedad (${metrics.pendingDaysCount} pend.)`,
      bg: [240, 253, 244],
      border: [34, 197, 94],
      text: [21, 128, 61]
    },
    {
      title: 'CASI INCIDENTES',
      value: `${metrics.nearMissCount}`,
      sub: 'Condición / Acto',
      bg: [254, 243, 199],
      border: [245, 158, 11],
      text: [180, 83, 9]
    },
    {
      title: '1ROS AUXILIOS',
      value: `${metrics.firstAidCount}`,
      sub: 'Atención en planta',
      bg: [239, 246, 255],
      border: [59, 130, 246],
      text: [29, 78, 216]
    },
    {
      title: 'ACCIDENTES LTI',
      value: `${metrics.lostTimeCount}`,
      sub: `${metrics.totalLostDays} días perdidos`,
      bg: [254, 242, 242],
      border: [239, 68, 68],
      text: [185, 28, 28]
    },
    {
      title: 'TOTAL EVENTOS',
      value: `${metrics.totalMonth}`,
      sub: `En ${monthName} ${year}`,
      bg: [248, 250, 252],
      border: [148, 163, 184],
      text: [30, 41, 59]
    }
  ];

  kpiData.forEach((kpi, idx) => {
    const kX = marginX + (idx * (kpiBoxWidth + 2));
    doc.setFillColor(kpi.bg[0], kpi.bg[1], kpi.bg[2]);
    doc.setDrawColor(kpi.border[0], kpi.border[1], kpi.border[2]);
    doc.setLineWidth(0.3);
    doc.roundedRect(kX, currentY, kpiBoxWidth, kpiHeight, 1, 1, 'FD');

    doc.setTextColor(100, 116, 139);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(5.5);
    doc.text(kpi.title, kX + 2, currentY + 3.2);

    doc.setTextColor(kpi.text[0], kpi.text[1], kpi.text[2]);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text(kpi.value, kX + 2, currentY + 8.5);

    doc.setTextColor(100, 116, 139);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(4.8);
    doc.text(kpi.sub, kX + 2, currentY + 12.8);
  });

  currentY += kpiHeight + 4.5;

  // Secondary statistical indices bar (IF, IG, HHT)
  doc.setFillColor(241, 245, 249);
  doc.setDrawColor(203, 213, 225);
  doc.roundedRect(marginX, currentY, contentWidth, 5.5, 1, 1, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.2);
  doc.setTextColor(51, 65, 85);
  doc.text(`Índice de Frecuencia (IF STPS): ${frequencyIndex}`, marginX + 3, currentY + 3.6);
  doc.text(`Índice de Severidad (IG STPS): ${severityIndex}`, marginX + 60, currentY + 3.6);
  doc.text(`Horas-Hombre Trabajadas (HHT Est.): ${estimatedHHT.toLocaleString()} hrs`, marginX + 115, currentY + 3.6);

  currentY += 8.5;

  // --- 2. REPRESENTACIÓN GRÁFICA VECTORIAL: CRUZ DE SEGURIDAD INDUSTRIAL ---
  currentY = drawSectionTitle(`2. Cruz de Seguridad Industrial Oficial — Periodo: ${monthName.toUpperCase()} ${year}`, currentY);

  // Background frame for the Safety Cross
  const crossFrameHeight = 84;
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.3);
  doc.roundedRect(marginX, currentY, contentWidth, crossFrameHeight, 1.5, 1.5, 'FD');

  // Traditional 31-cell Industrial Cross Grid
  const crossLayout = [
    [null, null, 1, 2, 3, null, null],
    [null, null, 4, 5, 6, null, null],
    [7, 8, 9, 10, 11, 12, 13],
    [14, 15, 16, 17, 18, 19, 20],
    [21, 22, 23, 24, 25, 26, 27],
    [null, null, 28, 29, 30, null, null],
    [null, null, 31, null, null, null, null],
  ];

  const cellWidth = 12.5;
  const cellHeight = 10;
  const gridStartX = marginX + ((contentWidth - (7 * cellWidth)) / 2);
  const gridStartY = currentY + 3;

  crossLayout.forEach((row, rIdx) => {
    row.forEach((dayNum, cIdx) => {
      if (dayNum === null) return;

      const cX = gridStartX + (cIdx * cellWidth);
      const cY = gridStartY + (rIdx * cellHeight);

      // Check if this day exists in this month (e.g. Feb 28/29, Apr 30)
      if (dayNum > calendarDays.length) {
        doc.setFillColor(241, 245, 249);
        doc.setDrawColor(226, 232, 240);
        doc.roundedRect(cX + 0.5, cY + 0.5, cellWidth - 1, cellHeight - 1, 0.8, 0.8, 'FD');
        doc.setTextColor(203, 213, 225);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6);
        doc.text(`${dayNum}`, cX + (cellWidth / 2) - 1.5, cY + (cellHeight / 2) + 1.5);
        return;
      }

      const dayObj = calendarDays.find(d => d.day === dayNum);
      const isFuture = dayObj?.isFuture ?? false;
      const incident = dayObj?.incident;

      // Determine cell colors according to safety standard
      let fillRGB: [number, number, number] = [236, 253, 245]; // Green tint default
      let strokeRGB: [number, number, number] = [16, 185, 129]; // Emerald-500
      let textRGB: [number, number, number] = [6, 78, 59];
      let statusTag = 'SEGURO';

      if (isFuture && !incident) {
        // Pending / Future Day: NOT GREEN
        fillRGB = [241, 245, 249]; // Slate-100
        strokeRGB = [203, 213, 225]; // Slate-300
        textRGB = [148, 163, 184];
        statusTag = 'PEND.';
      } else if (incident) {
        if (incident.classification === 'near_miss') {
          fillRGB = [254, 243, 199];
          strokeRGB = [245, 158, 11];
          textRGB = [146, 64, 14];
          statusTag = 'CASI INC.';
        } else if (incident.classification === 'first_aid') {
          fillRGB = [239, 246, 255];
          strokeRGB = [59, 130, 246];
          textRGB = [30, 64, 175];
          statusTag = '1ROS AUX.';
        } else if (incident.classification === 'medical_treatment') {
          fillRGB = [255, 237, 213];
          strokeRGB = [249, 115, 22];
          textRGB = [154, 52, 18];
          statusTag = 'ACC. S/TP';
        } else if (incident.classification === 'lost_time') {
          fillRGB = [254, 226, 226];
          strokeRGB = [239, 68, 68];
          textRGB = [153, 27, 27];
          statusTag = 'LTI INCAP.';
        } else if (incident.classification === 'environmental') {
          fillRGB = [243, 232, 255];
          strokeRGB = [168, 85, 247];
          textRGB = [107, 33, 168];
          statusTag = 'AMBIENTAL';
        }
      }

      // Draw cell box
      doc.setFillColor(fillRGB[0], fillRGB[1], fillRGB[2]);
      doc.setDrawColor(strokeRGB[0], strokeRGB[1], strokeRGB[2]);
      doc.setLineWidth(0.4);
      doc.roundedRect(cX + 0.5, cY + 0.5, cellWidth - 1, cellHeight - 1, 0.8, 0.8, 'FD');

      // Day number (Bold)
      doc.setTextColor(textRGB[0], textRGB[1], textRGB[2]);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.8);
      doc.text(`${dayNum}`, cX + 2, cY + 4.2);

      // Status tag
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(4.2);
      doc.text(statusTag, cX + 1.8, cY + 7.8);
    });
  });

  // Cross footer note
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(5.5);
  doc.setTextColor(100, 116, 139);
  doc.text('Estructura normalizada de 31 casillas. Las casillas en verde corresponden a jornadas operadas sin incidentes. Las casillas grises representan días aún por transcurrir.', marginX + 3, currentY + crossFrameHeight - 2.5);

  currentY += crossFrameHeight + 4.5;

  // --- 3. CÓDIGO DE COLORES NORMATIVO OFICIAL ---
  currentY = drawSectionTitle('3. Criterios Técnicos de Clasificación de Seguridad (NOM-019-STPS / NOM-021-STPS)', currentY);

  const legendBoxWidth = (contentWidth - 6) / 4;
  const legendBoxHeight = 12;

  const colorCodes = [
    {
      colorName: 'VERDE (Día Seguro)',
      norm: 'Operación Segura',
      desc: 'Jornada laboral sin accidentes ni actos o condiciones de riesgo.',
      fill: [236, 253, 245],
      border: [16, 185, 129],
      text: [4, 120, 87]
    },
    {
      colorName: 'AMARILLO (Casi Incidente)',
      norm: 'NOM-019-STPS',
      desc: 'Condición o acto inseguro detectado oportunamente sin lesión.',
      fill: [254, 243, 199],
      border: [245, 158, 11],
      text: [180, 83, 9]
    },
    {
      colorName: 'AZUL (1ros Auxilios)',
      norm: 'NOM-005-STPS',
      desc: 'Lesión leve atendida en botiquín de planta sin incapacidad.',
      fill: [239, 246, 255],
      border: [59, 130, 246],
      text: [29, 78, 216]
    },
    {
      colorName: 'ROJO (Accidente LTI)',
      norm: 'Formato CM-2A STPS',
      desc: 'Accidente con tiempo perdido e incapacidad médica temporal.',
      fill: [254, 242, 242],
      border: [239, 68, 68],
      text: [185, 28, 28]
    }
  ];

  colorCodes.forEach((code, idx) => {
    const lX = marginX + (idx * (legendBoxWidth + 2));
    doc.setFillColor(code.fill[0], code.fill[1], code.fill[2]);
    doc.setDrawColor(code.border[0], code.border[1], code.border[2]);
    doc.setLineWidth(0.3);
    doc.roundedRect(lX, currentY, legendBoxWidth, legendBoxHeight, 1, 1, 'FD');

    // Dot indicator
    doc.setFillColor(code.border[0], code.border[1], code.border[2]);
    doc.circle(lX + 3, currentY + 3.2, 1.2, 'F');

    doc.setTextColor(code.text[0], code.text[1], code.text[2]);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(5.8);
    doc.text(code.colorName, lX + 5.5, currentY + 3.6);

    doc.setTextColor(71, 85, 105);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(4.8);
    const descLines = doc.splitTextToSize(code.desc, legendBoxWidth - 4);
    doc.text(descLines, lX + 2, currentY + 6.8);
  });

  // ==========================================
  // PAGE 2: INCIDENT LOG TABLE AND SIGNATURES
  // ==========================================
  doc.addPage();

  // Page 2 Header
  doc.setFillColor(188, 17, 0);
  doc.rect(0, 0, pageWidth, 2.5, 'F');

  doc.setFillColor(15, 23, 42);
  doc.rect(0, 2.5, pageWidth, 12, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.text(`REPORTE MENSUAL DE CRUZ DE SEGURIDAD — FOLIO: ${folioCode}`, marginX, 9.5);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.8);
  doc.setTextColor(203, 213, 225);
  doc.text(`Bitácora de Siniestralidad y Validación de la Comisión Mixta de Seguridad e Higiene | Periodo: ${monthName} ${year}`, marginX + 110, 9.5);

  let page2Y = 19;

  // --- 4. BITÁCORA DETALLADA DE INCIDENTES DEL MES ---
  page2Y = drawSectionTitle(`4. Bitácora de Siniestralidad e Incidentes Registrados (${incidents.length} Eventos en ${monthName} ${year})`, page2Y);

  if (incidents.length === 0) {
    // Zero Incidents Banner
    doc.setFillColor(240, 253, 244); // Green-50
    doc.setDrawColor(187, 247, 208); // Green-200
    doc.setLineWidth(0.4);
    doc.roundedRect(marginX, page2Y, contentWidth, 22, 1.5, 1.5, 'FD');

    doc.setFillColor(16, 185, 129);
    doc.circle(marginX + 8, page2Y + 11, 4, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text('✓', marginX + 6.8, page2Y + 13.5);

    doc.setTextColor(6, 95, 70);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.text('PERIODO DE OPERACIÓN SEGURA — CERO ACCIDENTES', marginX + 16, page2Y + 7);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.8);
    doc.setTextColor(21, 128, 61);
    doc.text(`Durante el mes de ${monthName} de ${year} no se registraron accidentes incapacitantes (LTI), casi incidentes ni afectaciones ambientales en la sede ${costCenterName}.`, marginX + 16, page2Y + 12);
    doc.text('El centro de trabajo mantuvo 100% de cumplimiento en medidas preventivas y supervisión de seguridad.', marginX + 16, page2Y + 16.5);

    page2Y += 27;
  } else {
    // Table Header
    const colWidths = [18, 14, 26, 26, 44, 38, 12, 10];
    const colXPositions: number[] = [];
    let currentX = marginX;
    colWidths.forEach(w => {
      colXPositions.push(currentX);
      currentX += w;
    });

    doc.setFillColor(30, 41, 59); // Slate-800
    doc.rect(marginX, page2Y, contentWidth, 6, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(5.5);

    doc.text('FOLIO', colXPositions[0] + 1.5, page2Y + 4);
    doc.text('FECHA/HORA', colXPositions[1] + 1.5, page2Y + 4);
    doc.text('CLASIFICACIÓN', colXPositions[2] + 1.5, page2Y + 4);
    doc.text('ÁREA / PROCESO', colXPositions[3] + 1.5, page2Y + 4);
    doc.text('DESCRIPCIÓN DEL EVENTO', colXPositions[4] + 1.5, page2Y + 4);
    doc.text('CAUSA RAÍZ / ACCIÓN', colXPositions[5] + 1.5, page2Y + 4);
    doc.text('DÍAS INC.', colXPositions[6] + 1.5, page2Y + 4);
    doc.text('ESTATUS', colXPositions[7] + 1.5, page2Y + 4);

    page2Y += 6;

    // Render incident rows
    incidents.slice(0, 8).forEach((inc, idx) => {
      const isEven = idx % 2 === 0;
      doc.setFillColor(isEven ? 255 : 248, isEven ? 255 : 250, isEven ? 255 : 252);
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.2);

      const rowHeight = 11;
      doc.rect(marginX, page2Y, contentWidth, rowHeight, 'FD');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(5.5);
      doc.setTextColor(15, 23, 42);
      doc.text(inc.id || `INC-${inc.day}`, colXPositions[0] + 1.5, page2Y + 4);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(5);
      doc.setTextColor(71, 85, 105);
      doc.text(`${inc.date}`, colXPositions[1] + 1.5, page2Y + 3.8);
      doc.text(`${inc.time || 'N/R'}`, colXPositions[1] + 1.5, page2Y + 7.5);

      // Classification tag
      let classText = 'Día Seguro';
      if (inc.classification === 'near_miss') classText = 'Casi Incidente';
      else if (inc.classification === 'first_aid') classText = '1ros Auxilios';
      else if (inc.classification === 'medical_treatment') classText = 'Acc. s/TP';
      else if (inc.classification === 'lost_time') classText = 'Acc. LTI Incap.';
      else if (inc.classification === 'environmental') classText = 'Ambiental';

      doc.setFont('helvetica', 'bold');
      doc.setTextColor(188, 17, 0);
      doc.text(classText, colXPositions[2] + 1.5, page2Y + 4);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 116, 139);
      doc.text(`Sev: ${(inc.severity || 'Media').toUpperCase()}`, colXPositions[2] + 1.5, page2Y + 7.5);

      // Area
      doc.setTextColor(15, 23, 42);
      doc.setFont('helvetica', 'normal');
      const areaLines = doc.splitTextToSize(inc.area || 'Planta Operativa', colWidths[3] - 3);
      doc.text(areaLines, colXPositions[3] + 1.5, page2Y + 3.8);

      // Description
      const descLines = doc.splitTextToSize(inc.description || inc.title || 'Sin descripción', colWidths[4] - 3);
      doc.text(descLines.slice(0, 2), colXPositions[4] + 1.5, page2Y + 3.8);

      // Cause / Action
      const causeText = inc.rootCause || inc.immediateAction || 'En investigación HSEQ';
      const causeLines = doc.splitTextToSize(causeText, colWidths[5] - 3);
      doc.text(causeLines.slice(0, 2), colXPositions[5] + 1.5, page2Y + 3.8);

      // Lost days
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(inc.lostDays > 0 ? 220 : 71, inc.lostDays > 0 ? 38 : 85, inc.lostDays > 0 ? 38 : 105);
      doc.text(`${inc.lostDays || 0}`, colXPositions[6] + 3.5, page2Y + 5.5);

      // Status
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(16, 185, 129);
      doc.text((inc.actionStatus || 'Cerrado').toUpperCase().substring(0, 8), colXPositions[7] + 1, page2Y + 5.5);

      page2Y += rowHeight;
    });

    page2Y += 4;
  }

  // --- 5. EVALUACIÓN DE MEDIDAS PREVENTIVAS Y PLÁTICAS DE 5 MINUTOS ---
  page2Y = drawSectionTitle('5. Cumplimiento de Pláticas de 5 Minutos y Actividades Preventivas (NOM-019-STPS)', page2Y);

  const prevBoxWidth = (contentWidth - 4) / 3;
  const prevBoxHeight = 16;

  // Box 1: Pláticas 5 min
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(203, 213, 225);
  doc.roundedRect(marginX, page2Y, prevBoxWidth, prevBoxHeight, 1, 1, 'FD');
  doc.setTextColor(15, 23, 42);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.2);
  doc.text('PLÁTICAS DE SEGURIDAD (5 MIN)', marginX + 2.5, page2Y + 3.8);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(5.5);
  doc.setTextColor(71, 85, 105);
  doc.text('• Cobertura en turnos: 100% de personal', marginX + 2.5, page2Y + 7.5);
  doc.text('• Temas: Riesgo eléctrico, EPP y ergonomía', marginX + 2.5, page2Y + 11.2);
  doc.text('• Registros DC-3 y listas firmadas en archivo', marginX + 2.5, page2Y + 14.5);

  // Box 2: Inspecciones de Seguridad
  doc.roundedRect(marginX + prevBoxWidth + 2, page2Y, prevBoxWidth, prevBoxHeight, 1, 1, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.2);
  doc.setTextColor(15, 23, 42);
  doc.text('RECORRIDOS DE LA COMISIÓN MIXTA', marginX + prevBoxWidth + 4.5, page2Y + 3.8);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(5.5);
  doc.setTextColor(71, 85, 105);
  doc.text('• Recorrido mensual NOM-019 ejecutado', marginX + prevBoxWidth + 4.5, page2Y + 7.5);
  doc.text('• Verificación de extintores y botiquines: Conforme', marginX + prevBoxWidth + 4.5, page2Y + 11.2);
  doc.text('• Orden y limpieza (5S) en líneas: Aprobado', marginX + prevBoxWidth + 4.5, page2Y + 14.5);

  // Box 3: Dictamen de Comisión
  doc.roundedRect(marginX + (prevBoxWidth * 2) + 4, page2Y, prevBoxWidth, prevBoxHeight, 1, 1, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.2);
  doc.setTextColor(15, 23, 42);
  doc.text('DICTAMEN Y CUMPLIMIENTO LEGAL', marginX + (prevBoxWidth * 2) + 6.5, page2Y + 3.8);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(5.5);
  doc.setTextColor(71, 85, 105);
  doc.text('• Cumplimiento NOM-019 / NOM-021: 100%', marginX + (prevBoxWidth * 2) + 6.5, page2Y + 7.5);
  doc.text('• Matriz de riesgos actualizada en el periodo', marginX + (prevBoxWidth * 2) + 6.5, page2Y + 11.2);
  doc.text('• Estatus general de sede: OPERACIÓN SEGURA', marginX + (prevBoxWidth * 2) + 6.5, page2Y + 14.5);

  page2Y += prevBoxHeight + 6;

  // --- 6. BLOQUE FORMAL DE FIRMAS DE LA COMISIÓN MIXTA (NOM-019-STPS) ---
  page2Y = drawSectionTitle('6. Firmas de Validación Oficial de la Comisión de Seguridad e Higiene (NOM-019-STPS)', page2Y);

  const sigWidth = (contentWidth - 9) / 4;
  const sigY = page2Y + 16;

  // Signature 1: Coordinador Comisión (Patrón)
  doc.setDrawColor(100, 116, 139);
  doc.setLineWidth(0.4);
  doc.line(marginX, sigY, marginX + sigWidth, sigY);
  doc.setTextColor(15, 23, 42);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.2);
  doc.text('COORDINADOR DE COMISIÓN', marginX, sigY + 3.2);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(5.2);
  doc.setTextColor(71, 85, 105);
  doc.text('Representante Patronal', marginX, sigY + 6.5);
  doc.text('Comisión Mixta NOM-019', marginX, sigY + 9.5);

  // Signature 2: Secretario Comisión (Trabajadores)
  const sig2X = marginX + sigWidth + 3;
  doc.line(sig2X, sigY, sig2X + sigWidth, sigY);
  doc.setTextColor(15, 23, 42);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.2);
  doc.text('SECRETARIO DE COMISIÓN', sig2X, sigY + 3.2);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(5.2);
  doc.setTextColor(71, 85, 105);
  doc.text('Representante de Trabajadores', sig2X, sigY + 6.5);
  doc.text('Comisión Mixta NOM-019', sig2X, sigY + 9.5);

  // Signature 3: Responsable HSEQ
  const sig3X = marginX + ((sigWidth + 3) * 2);
  doc.line(sig3X, sigY, sig3X + sigWidth, sigY);
  doc.setTextColor(15, 23, 42);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.2);
  doc.text('RESPONSABLE HSEQ', sig3X, sigY + 3.2);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(5.2);
  doc.setTextColor(71, 85, 105);
  doc.text('Coordinador de Seguridad Industrial', sig3X, sigY + 6.5);
  doc.text('SNF S.A. de C.V.', sig3X, sigY + 9.5);

  // Signature 4: Gerente de Planta
  const sig4X = marginX + ((sigWidth + 3) * 3);
  doc.line(sig4X, sigY, sig4X + sigWidth, sigY);
  doc.setTextColor(15, 23, 42);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.2);
  doc.text('GERENTE DE PLANTA / OPERACIONES', sig4X, sigY + 3.2);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(5.2);
  doc.setTextColor(71, 85, 105);
  doc.text('Dirección Operativa', sig4X, sigY + 6.5);
  doc.text('Servicios Nuevos de Filtración', sig4X, sigY + 9.5);

  // --- FOOTERS ON ALL PAGES ---
  const totalPages = (doc as any).internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);

    // Bottom accent line
    doc.setFillColor(188, 17, 0);
    doc.rect(0, pageHeight - 3, pageWidth, 3, 'F');

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(5.8);
    doc.setTextColor(148, 163, 184);
    doc.text(`Reporte Oficial Mensual de Cruz de Seguridad Industrial - Folio: ${folioCode} | Cumplimiento estricto con NOM-019-STPS / NOM-021-STPS / ISO 45001.`, marginX, pageHeight - 5);
    doc.text(`Página ${i} de ${totalPages}`, pageWidth - marginX - 16, pageHeight - 5);
  }

  // Download PDF
  const sanitizedSede = (costCenterCode || 'SEDE').replace(/[^a-zA-Z0-9-_]/g, '_');
  const filename = `Reporte_Cruz_Seguridad_${year}_${monthNumber}_${sanitizedSede}.pdf`;
  doc.save(filename);
}

export interface HSEQExecutiveDashboardPDFData {
  costCenterName: string;
  costCenterCode: string;
  daysWithoutIncident: number;
  recordDays: number;
  totalIncidentsCount: number;
  incidents: SafetyIncident[];
  safetyTalks: {
    id: string;
    topic: string;
    date: string;
    facilitator: string;
    attendeesCount: number;
    area: string;
    status: string;
  }[];
  certificates: {
    id: string;
    name: string;
    issuer: string;
    code: string;
    category: string;
    validUntil: string;
    status: string;
  }[];
  dc3Records: {
    id: string;
    employeeName: string;
    courseName: string;
    trainer: string;
    expiryDate: string;
    status: string;
  }[];
}

/**
 * Generates an official HSEQ Executive Dashboard PDF report
 * containing KPIs, safety talks summary, active certifications, DC-3 records, and incident stats.
 */
export async function generateHSEQExecutiveDashboardPDF(data: HSEQExecutiveDashboardPDFData): Promise<void> {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'letter' // 216mm x 279mm
  });

  const pageWidth = 216;
  const pageHeight = 279;
  const marginX = 14;
  const contentWidth = pageWidth - (marginX * 2); // 188mm

  const today = new Date();
  const currentDateFormatted = today.toLocaleDateString('es-MX', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });
  const folioCode = `HSEQ-EJEC-${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(Date.now()).slice(-4)}`;

  // Calculate talk metrics
  const totalTalks = data.safetyTalks.length;
  const totalAttendees = data.safetyTalks.reduce((sum, t) => sum + (Number(t.attendeesCount) || 0), 0);
  const completedTalks = data.safetyTalks.filter(t => t.status === 'completada').length;

  // Calculate certificate metrics
  const activeCertificates = data.certificates.filter(c => c.status === 'activo').length;

  // Calculate incident stats
  const ltiCount = data.incidents.filter(i => i.classification === 'lost_time').length;
  const nearMissCount = data.incidents.filter(i => i.classification === 'near_miss').length;
  const firstAidCount = data.incidents.filter(i => i.classification === 'first_aid').length;
  const medTreatCount = data.incidents.filter(i => i.classification === 'medical_treatment').length;
  const envCount = data.incidents.filter(i => i.classification === 'environmental').length;

  const drawSectionHeader = (title: string, yPos: number): number => {
    doc.setFillColor(241, 245, 249);
    doc.roundedRect(marginX, yPos, contentWidth, 6, 1, 1, 'F');
    doc.setFillColor(188, 17, 0);
    doc.rect(marginX, yPos, 2.5, 6, 'F');
    doc.setTextColor(15, 23, 42);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.8);
    doc.text(title.toUpperCase(), marginX + 5, yPos + 4.2);
    return yPos + 8;
  };

  // Top Accent bar
  doc.setFillColor(188, 17, 0);
  doc.rect(0, 0, pageWidth, 3.5, 'F');

  // Main Header Box
  doc.setFillColor(15, 23, 42);
  doc.rect(0, 3.5, pageWidth, 32, 'F');

  // Corporate Logo Box
  try {
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(marginX, 6.5, 26, 26, 2, 2, 'F');
    doc.addImage(CORPORATE_LOGO_BASE64, 'JPEG', marginX + 1, 7.5, 24, 24);
  } catch {
    doc.setFillColor(188, 17, 0);
    doc.roundedRect(marginX, 7.5, 22, 23, 2, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('SNF', marginX + 4, 20);
  }

  // Company Name & Header
  const textStartX = marginX + 29;
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('SERVICIOS NUEVOS DE FILTRACIÓN S.A. DE C.V.', textStartX, 12);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.8);
  doc.setTextColor(203, 213, 225);
  doc.text('DEPARTAMENTO DE SEGURIDAD INDUSTRIAL, SALUD OCUPACIONAL Y MEDIO AMBIENTE (HSEQ)', textStartX, 16.5);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(52, 211, 153);
  doc.text('INFORME EJECUTIVO DE SEGURIDAD INDUSTRIAL, CERTIFICACIONES Y CAPACITACIÓN HSEQ', textStartX, 22.5);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.setTextColor(148, 163, 184);
  doc.text(`Sede: ${data.costCenterName} (${data.costCenterCode})  |  ISO 45001 / ISO 14001 / ISO 9001 / NOM-019-STPS`, textStartX, 27.5);

  // Folio Box
  doc.setFillColor(30, 41, 59);
  doc.setDrawColor(52, 211, 153);
  doc.setLineWidth(0.4);
  doc.roundedRect(pageWidth - marginX - 48, 6.5, 48, 26, 2, 2, 'FD');

  doc.setTextColor(148, 163, 184);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(5.8);
  doc.text('FOLIO EJECUTIVO HSEQ', pageWidth - marginX - 45, 10.5);

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.text(folioCode, pageWidth - marginX - 45, 15.5);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6);
  doc.setTextColor(226, 232, 240);
  doc.text(`Fecha: ${currentDateFormatted}`, pageWidth - marginX - 45, 20.5);
  doc.text(`Estatus: VIGENTE / AUDITADO`, pageWidth - marginX - 45, 25);

  let currentY = 38.5;

  // --- 1. TABLERO DE INDICADORES CLAVE (KPIS) ---
  currentY = drawSectionHeader('1. Indicadores Clave de Desempeño y Siniestralidad (KPIs)', currentY);

  const kpiBoxWidth = (contentWidth - 8) / 5;
  const kpiHeight = 15;

  const kpis = [
    {
      title: 'DÍAS SIN ACCIDENTE (LTI)',
      value: `${data.daysWithoutIncident}`,
      sub: `Récord: ${data.recordDays} días`,
      bg: [236, 253, 245],
      border: [16, 185, 129],
      text: [4, 120, 87]
    },
    {
      title: 'PLÁTICAS DE 5 MIN',
      value: `${totalTalks}`,
      sub: `${totalAttendees} participantes`,
      bg: [239, 246, 255],
      border: [59, 130, 246],
      text: [29, 78, 216]
    },
    {
      title: 'CERTIFICADOS ACTIVOS',
      value: `${activeCertificates}`,
      sub: 'ISO / STPS 100% Vigentes',
      bg: [254, 243, 199],
      border: [245, 158, 11],
      text: [180, 83, 9]
    },
    {
      title: 'CONSTANCIAS DC-3 STPS',
      value: `${data.dc3Records.length}`,
      sub: 'Padrón de competencias',
      bg: [245, 243, 255],
      border: [139, 92, 246],
      text: [109, 40, 217]
    },
    {
      title: 'ÍNDICE DE FRECUENCIA (IF)',
      value: '0.00',
      sub: 'Cero incapacitantes',
      bg: [240, 253, 244],
      border: [34, 197, 94],
      text: [21, 128, 61]
    }
  ];

  kpis.forEach((k, idx) => {
    const kX = marginX + (idx * (kpiBoxWidth + 2));
    doc.setFillColor(k.bg[0], k.bg[1], k.bg[2]);
    doc.setDrawColor(k.border[0], k.border[1], k.border[2]);
    doc.setLineWidth(0.3);
    doc.roundedRect(kX, currentY, kpiBoxWidth, kpiHeight, 1, 1, 'FD');

    doc.setTextColor(100, 116, 139);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(5);
    doc.text(k.title, kX + 2, currentY + 3.2);

    doc.setTextColor(k.text[0], k.text[1], k.text[2]);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text(k.value, kX + 2, currentY + 8.5);

    doc.setTextColor(100, 116, 139);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(4.8);
    doc.text(k.sub, kX + 2, currentY + 12.8);
  });

  currentY += kpiHeight + 5;

  // --- 2. PADRÓN DE CERTIFICACIONES Y AUDITORÍAS HSEQ ---
  currentY = drawSectionHeader('2. Padrón Oficial de Certificaciones de Calidad, Ambiental y Seguridad', currentY);

  // Table header
  doc.setFillColor(30, 41, 59);
  doc.rect(marginX, currentY, contentWidth, 5, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(5.8);

  const colCerts = [
    { label: 'NORMA / CERTIFICACIÓN', x: marginX + 2, w: 70 },
    { label: 'ORGANISMO EMISOR', x: marginX + 74, w: 42 },
    { label: 'FOLIO / CÓDIGO', x: marginX + 118, w: 34 },
    { label: 'VIGENCIA', x: marginX + 154, w: 18 },
    { label: 'ESTATUS', x: marginX + 174, w: 14 }
  ];

  colCerts.forEach(c => doc.text(c.label, c.x, currentY + 3.4));
  currentY += 5;

  data.certificates.forEach((cert, idx) => {
    const rowBg = idx % 2 === 0 ? [255, 255, 255] : [248, 250, 252];
    doc.setFillColor(rowBg[0], rowBg[1], rowBg[2]);
    doc.rect(marginX, currentY, contentWidth, 5.5, 'F');
    doc.setDrawColor(226, 232, 240);
    doc.line(marginX, currentY + 5.5, marginX + contentWidth, currentY + 5.5);

    doc.setTextColor(15, 23, 42);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(5.8);
    doc.text(cert.name.slice(0, 48), marginX + 2, currentY + 3.8);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(5.5);
    doc.setTextColor(71, 85, 105);
    doc.text(cert.issuer.slice(0, 28), marginX + 74, currentY + 3.8);

    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 41, 59);
    doc.text(cert.code, marginX + 118, currentY + 3.8);

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(71, 85, 105);
    doc.text(cert.validUntil, marginX + 154, currentY + 3.8);

    doc.setFillColor(236, 253, 245);
    doc.setDrawColor(16, 185, 129);
    doc.roundedRect(marginX + 173, currentY + 1, 13, 3.5, 0.5, 0.5, 'FD');
    doc.setTextColor(4, 120, 87);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(4.8);
    doc.text(cert.status.toUpperCase(), marginX + 175, currentY + 3.5);

    currentY += 5.5;
  });

  currentY += 4.5;

  // --- 3. PROGRAMA DE PLÁTICAS DE SEGURIDAD (5 MINUTOS) ---
  currentY = drawSectionHeader('3. Registro y Cobertura de Pláticas de Seguridad de 5 Minutos', currentY);

  // Table header
  doc.setFillColor(30, 41, 59);
  doc.rect(marginX, currentY, contentWidth, 5, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(5.8);

  const colTalks = [
    { label: 'FOLIO', x: marginX + 2, w: 20 },
    { label: 'TEMA DE PREVENCIÓN', x: marginX + 24, w: 76 },
    { label: 'FACILITADOR / RESPONSABLE', x: marginX + 102, w: 40 },
    { label: 'FECHA', x: marginX + 144, w: 18 },
    { label: 'ASISTENTES', x: marginX + 164, w: 24 }
  ];

  colTalks.forEach(c => doc.text(c.label, c.x, currentY + 3.4));
  currentY += 5;

  const recentTalks = data.safetyTalks.slice(0, 5);
  recentTalks.forEach((talk, idx) => {
    const rowBg = idx % 2 === 0 ? [255, 255, 255] : [248, 250, 252];
    doc.setFillColor(rowBg[0], rowBg[1], rowBg[2]);
    doc.rect(marginX, currentY, contentWidth, 5.5, 'F');
    doc.setDrawColor(226, 232, 240);
    doc.line(marginX, currentY + 5.5, marginX + contentWidth, currentY + 5.5);

    doc.setTextColor(188, 17, 0);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(5.5);
    doc.text(talk.id, marginX + 2, currentY + 3.8);

    doc.setTextColor(15, 23, 42);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(5.5);
    doc.text(talk.topic.slice(0, 52), marginX + 24, currentY + 3.8);

    doc.setTextColor(71, 85, 105);
    doc.text(talk.facilitator.slice(0, 28), marginX + 102, currentY + 3.8);

    doc.text(talk.date, marginX + 144, currentY + 3.8);

    doc.setFont('helvetica', 'bold');
    doc.setTextColor(4, 120, 87);
    doc.text(`${talk.attendeesCount} Colaboradores`, marginX + 164, currentY + 3.8);

    currentY += 5.5;
  });

  currentY += 4.5;

  // --- 4. RESUMEN DE SINIESTRALIDAD Y EVENTOS REGISTRADOS ---
  currentY = drawSectionHeader('4. Desglose de Siniestralidad y Pirámide de Control de Eventos', currentY);

  const eventBoxWidth = (contentWidth - 8) / 5;
  const eventHeight = 13;

  const eventCategories = [
    { title: 'ACCIDENTES LTI', count: ltiCount, label: 'Incapacitantes', bg: [254, 242, 242], border: [239, 68, 68], text: [185, 28, 28] },
    { title: 'SIN TIEMPO PERDIDO', count: medTreatCount, label: 'Atención Médica', bg: [255, 247, 237], border: [249, 115, 22], text: [194, 65, 12] },
    { title: 'PRIMEROS AUXILIOS', count: firstAidCount, label: 'Botiquín / Planta', bg: [239, 246, 255], border: [59, 130, 246], text: [29, 78, 216] },
    { title: 'CASI INCIDENTES', count: nearMissCount, label: 'Actos / Condiciones', bg: [254, 243, 199], border: [245, 158, 11], text: [180, 83, 9] },
    { title: 'AMBIENTALES', count: envCount, label: 'Derrames / Emisiones', bg: [250, 245, 255], border: [168, 85, 247], text: [126, 34, 206] }
  ];

  eventCategories.forEach((cat, idx) => {
    const eX = marginX + (idx * (eventBoxWidth + 2));
    doc.setFillColor(cat.bg[0], cat.bg[1], cat.bg[2]);
    doc.setDrawColor(cat.border[0], cat.border[1], cat.border[2]);
    doc.setLineWidth(0.3);
    doc.roundedRect(eX, currentY, eventBoxWidth, eventHeight, 1, 1, 'FD');

    doc.setTextColor(100, 116, 139);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(5);
    doc.text(cat.title, eX + 2, currentY + 3.2);

    doc.setTextColor(cat.text[0], cat.text[1], cat.text[2]);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.text(`${cat.count} Eventos`, eX + 2, currentY + 7.8);

    doc.setTextColor(100, 116, 139);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(4.8);
    doc.text(cat.label, eX + 2, currentY + 11.2);
  });

  currentY += eventHeight + 5;

  // --- 5. DECLARACIÓN NORMATIVA Y FIRMAS FORMALES ---
  currentY = drawSectionHeader('5. Validación Normativa y Firmas de la Comisión Mixta (NOM-019-STPS)', currentY);

  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(203, 213, 225);
  doc.setLineWidth(0.3);
  doc.roundedRect(marginX, currentY, contentWidth, 11, 1, 1, 'FD');

  doc.setTextColor(71, 85, 105);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(5.5);
  doc.text(
    'Se certifica que la información presentada en este informe ejecutivo corresponde a los registros auditados en planta conforme al Sistema de Gestión ISO 45001:2018 y las normas oficiales mexicanas NOM-019-STPS-2011, NOM-018-STPS-2015 y NOM-006-STPS-2014.',
    marginX + 3,
    currentY + 4,
    { maxWidth: contentWidth - 6 }
  );

  currentY += 14;

  // Four signature columns
  const sigWidth = (contentWidth - 9) / 4;
  const sigY = currentY + 13;

  const signatures = [
    { title: 'COORDINADOR COMISIÓN', sub: 'Representante Patronal NOM-019' },
    { title: 'SECRETARIO COMISIÓN', sub: 'Representante Trabajadores NOM-019' },
    { title: 'RESPONSABLE HSEQ', sub: 'Coordinador de Seguridad Industrial' },
    { title: 'GERENCIA DE PLANTA', sub: 'Dirección Operativa SNF' }
  ];

  signatures.forEach((sig, idx) => {
    const sX = marginX + (idx * (sigWidth + 3));
    doc.setDrawColor(100, 116, 139);
    doc.setLineWidth(0.4);
    doc.line(sX, sigY, sX + sigWidth, sigY);

    doc.setTextColor(15, 23, 42);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6);
    doc.text(sig.title, sX, sigY + 3.2);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(5);
    doc.setTextColor(71, 85, 105);
    doc.text(sig.sub, sX, sigY + 6.5);
    doc.text('Servicios Nuevos de Filtración', sX, sigY + 9.5);
  });

  // Footer Accent
  doc.setFillColor(188, 17, 0);
  doc.rect(0, pageHeight - 3, pageWidth, 3, 'F');

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(5.8);
  doc.setTextColor(148, 163, 184);
  doc.text(`Informe Ejecutivo de Seguridad HSEQ - Folio: ${folioCode} | Cumplimiento estricto con NOM-019-STPS / NOM-021-STPS / ISO 45001.`, marginX, pageHeight - 5);
  doc.text('Página 1 de 1', pageWidth - marginX - 16, pageHeight - 5);

  // Save PDF
  const sanitizedSede = (data.costCenterCode || 'SEDE').replace(/[^a-zA-Z0-9-_]/g, '_');
  const filename = `Dashboard_Ejecutivo_HSEQ_${sanitizedSede}_${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}.pdf`;
  doc.save(filename);
}

/**
 * Formats a date string (YYYY-MM-DD) into DD/MM/YYYY format
 */
function formatDateToDMY(dateStr?: string): string {
  if (!dateStr) return '';
  if (dateStr.includes('/')) return dateStr;
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return dateStr;
}

/**
 * Generates the official Safety Talk Report PDF (Reporte de Plática de 5 Minutos)
 * matching the standardized corporate format:
 * - Header: Reporte de Plática de 5 Minutos / Servicios Nuevos De Filtración Hispanomexicános
 * - Metadata Box: Fecha, Unidad de Negocio, Proyecto, Nombre de la Plática, Exponente
 * - Objetivo
 * - Tabla de Asistencia y Aceptación con 15 registros (REGISTRO, NOMBRE, RECIBÍ LA INFORMACIÓN Y ACEPTO..., FIRMA, FECHA)
 * - Cuadros inferiores de firmas: RRHH, GERENTE DE CUENTA, y FORMATO A PRUEBA DE CALIDAD ISO 9001
 * - Anexo fotográfico en Página 2 si existen evidencias fotográficas adjuntas.
 */
export async function generateSafetyTalkPDF(talk: SafetyTalk, sedeTitle?: string): Promise<void> {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'letter' // 216mm x 279mm
  });

  const pageWidth = 216;
  const pageHeight = 279;
  const marginX = 16;
  const contentWidth = pageWidth - (marginX * 2); // 184mm

  // Format date as DD/MM/YYYY
  const formattedDate = formatDateToDMY(talk.date) || '29/04/2026';
  const unidadNegocio = talk.businessUnit || talk.costCenterName || sedeTitle || 'Bajío';
  const proyecto = talk.project || talk.costCenterName || sedeTitle || 'Bajío';
  const nombrePlatica = talk.topic || 'Uso metodología 5S';
  const exponente = talk.facilitator || 'Alondra Tovar';
  const objetivo = talk.objective || talk.description || 'El operador entiende y aplica de forma correcta la metodología de las 5S en su espacio de trabajo, así como en su vida cotidiana';

  // --- 1. ENCABEZADO PRINCIPAL CENTRADO ---
  let currentY = 18;

  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('Reporte de Plática de 5 Minutos', pageWidth / 2, currentY, { align: 'center' });

  currentY += 7;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.text('Servicios Nuevos De Filtración Hispanomexicános', pageWidth / 2, currentY, { align: 'center' });

  currentY += 10;

  // --- 2. TABLA SUPERIOR DE DATOS GENERALES ---
  const infoTableX = marginX;
  const infoTableY = currentY;
  const infoTableW = contentWidth;
  const labelColW = 48;
  const valueColW = infoTableW - labelColW;
  const rowH = 6.2;
  const totalInfoRows = 5;
  const totalInfoH = rowH * totalInfoRows;

  const infoRows = [
    { label: 'Fecha:', value: formattedDate },
    { label: 'Unidad de Negocio:', value: unidadNegocio },
    { label: 'Proyecto:', value: proyecto },
    { label: 'Nombre de la Plática:', value: nombrePlatica },
    { label: 'Exponente:', value: exponente }
  ];

  // Outer border & background
  doc.setDrawColor(180, 180, 180);
  doc.setLineWidth(0.3);
  doc.rect(infoTableX, infoTableY, infoTableW, totalInfoH);

  // Vertical line
  doc.line(infoTableX + labelColW, infoTableY, infoTableX + labelColW, infoTableY + totalInfoH);

  infoRows.forEach((row, idx) => {
    const yPos = infoTableY + (idx * rowH);
    
    // Horizontal row line (except bottom border which is covered by rect)
    if (idx > 0) {
      doc.line(infoTableX, yPos, infoTableX + infoTableW, yPos);
    }

    // Label
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    doc.text(row.label, infoTableX + 3, yPos + 4.4);

    // Value
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    doc.text(row.value, infoTableX + labelColW + 3, yPos + 4.4);
  });

  currentY = infoTableY + totalInfoH + 8;

  // --- 3. SECCIÓN DE OBJETIVO ---
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  doc.text('Objetivo:', marginX, currentY);

  currentY += 4.5;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.8);
  doc.setTextColor(0, 0, 0);
  const objetivoLines = doc.splitTextToSize(objetivo, contentWidth);
  doc.text(objetivoLines, marginX, currentY);

  currentY += (objetivoLines.length * 4.2) + 5;

  // --- 4. TABLA DE ASISTENCIA Y ACEPTACIÓN (15 REGISTROS) ---
  const tableX = marginX;
  const tableY = currentY;
  const col1W = 18; // REGISTRO
  const col2W = 76; // NOMBRE
  const col3W = 32; // RECIBÍ LA INFORMACIÓN... (subdivided in 16mm SI, 16mm NO)
  const col3SubW = 16;
  const col4W = 34; // FIRMA
  const col5W = 24; // FECHA
  // Total width: 18 + 76 + 32 + 34 + 24 = 184mm

  const headerH = 15; // 2-level header height
  const tableRowH = 6.2;
  const numRows = 15;
  const tableRowsH = numRows * tableRowH;
  const fullTableH = headerH + tableRowsH;

  // Outer border of attendance table
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.4);
  doc.rect(tableX, tableY, contentWidth, fullTableH);

  // Header division lines
  const c1 = tableX + col1W;
  const c2 = c1 + col2W;
  const c3 = c2 + col3W;
  const c3Sub = c2 + col3SubW;
  const c4 = c3 + col4W;

  // Vertical column borders throughout full table
  doc.setLineWidth(0.3);
  doc.line(c1, tableY, c1, tableY + fullTableH);
  doc.line(c2, tableY, c2, tableY + fullTableH);
  doc.line(c3, tableY, c3, tableY + fullTableH);
  doc.line(c4, tableY, c4, tableY + fullTableH);

  // Column 3 sub-division for SI / NO (starts below header text, from tableY + 10 to bottom)
  doc.line(c3Sub, tableY + 10, c3Sub, tableY + fullTableH);

  // Header horizontal line between title and SI/NO
  doc.line(c2, tableY + 10, c3, tableY + 10);
  // Header bottom line
  doc.line(tableX, tableY + headerH, tableX + contentWidth, tableY + headerH);

  // Print Header Texts
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(0, 0, 0);

  // REGISTRO
  doc.text('REGISTRO', tableX + (col1W / 2), tableY + 8.5, { align: 'center' });

  // NOMBRE
  doc.text('NOMBRE', c1 + (col2W / 2), tableY + 8.5, { align: 'center' });

  // RECIBÍ LA INFORMACIÓN Y ACEPTO LO ESTABLECIDO EN LOS DOCUMENTOS
  doc.setFontSize(6);
  doc.text('RECIBÍ LA', c2 + (col3W / 2), tableY + 3.2, { align: 'center' });
  doc.text('INFORMACIÓN Y', c2 + (col3W / 2), tableY + 5.5, { align: 'center' });
  doc.text('ACEPTO LO', c2 + (col3W / 2), tableY + 7.6, { align: 'center' });
  doc.text('ESTABLECIDO', c2 + (col3W / 2), tableY + 9.5, { align: 'center' });
  //doc.text('DOCUMENTOS', c2 + (col3W / 2), tableY + 9.8, { align: 'center' });

  // Sub headers SI / NO
  doc.setFontSize(7);
  doc.text('SI', c2 + (col3SubW / 2), tableY + 13.5, { align: 'center' });
  doc.text('NO', c3Sub + (col3SubW / 2), tableY + 13.5, { align: 'center' });

  // FIRMA
  doc.setFontSize(7.5);
  doc.text('FIRMA', c3 + (col4W / 2), tableY + 8.5, { align: 'center' });

  // FECHA
  doc.text('FECHA', c4 + (col5W / 2), tableY + 8.5, { align: 'center' });

  // Render 15 Rows
  const attendees = talk.attendees || [];
  
  for (let i = 1; i <= numRows; i++) {
    const rowY = tableY + headerH + ((i - 1) * tableRowH);

    // Horizontal row divider
    if (i < numRows) {
      doc.line(tableX, rowY + tableRowH, tableX + contentWidth, rowY + tableRowH);
    }

    // Number (1 to 15)
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.text(String(i), tableX + (col1W / 2), rowY + 4.3, { align: 'center' });

    // Attendee Data if provided
    const att = attendees[i - 1];
    if (att) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      doc.text(att.name || '', c1 + 2, rowY + 4.3);

      if (att.accepted === 'NO') {
        doc.text('NO', c3Sub + (col3SubW / 2), rowY + 4.3, { align: 'center' });
      } else {
        doc.text('SI', c2 + (col3SubW / 2), rowY + 4.3, { align: 'center' });
      }

      if (att.date || formattedDate) {
        doc.setFontSize(7);
        doc.text(att.date ? formatDateToDMY(att.date) : formattedDate, c4 + (col5W / 2), rowY + 4.3, { align: 'center' });
      }
    } else {
      // Default: printed SI/NO options for paper form signing
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.text('SI', c2 + (col3SubW / 2), rowY + 4.3, { align: 'center' });
      doc.text('NO', c3Sub + (col3SubW / 2), rowY + 4.3, { align: 'center' });
    }
  }

  // --- 5. CUADROS INFERIORES DE FIRMAS Y CONTROL DE CALIDAD ---
  const bottomY = pageHeight - 32;
  const boxHeight = 15;

  // Box 1: RRHH (Left)
  const b1W = 50;
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.3);
  doc.rect(marginX, bottomY, b1W, boxHeight);

  // Line for signature
  doc.line(marginX + 5, bottomY + 10, marginX + b1W - 5, bottomY + 10);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.text('RRHH', marginX + (b1W / 2), bottomY + 13.5, { align: 'center' });

  // Box 2: GERENTE DE CUENTA (Center)
  const b2X = marginX + 57;
  const b2W = 48;
  doc.rect(b2X, bottomY, b2W, boxHeight);

  // Line for signature
  doc.line(b2X + 5, bottomY + 10, b2X + b2W - 5, bottomY + 10);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.text('GERENTE DE CUENTA', b2X + (b2W / 2), bottomY + 13.5, { align: 'center' });

  // Box 3: FORMATO A PRUEBA DE CALIDAD / ISO 9001 (Right)
  const b3X = b2X + b2W + 2;
  const b3W = contentWidth - (b3X - marginX);
  doc.rect(b3X, bottomY, b3W, boxHeight);

  // Optional vertical divider in Box 3 like in image
  doc.line(b3X + 2, bottomY, b3X + 2, bottomY + boxHeight);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.text('FORMATO A PRUEBA DE CALIDAD', b3X + (b3W / 2) + 1, bottomY + 6.5, { align: 'center' });
  doc.setFontSize(7);
  doc.text('ISO 9001', b3X + (b3W / 2) + 1, bottomY + 11.5, { align: 'center' });

  // --- 6. ANEXO FOTOGRÁFICO (SI HAY FOTOS ADJUNTAS) ---
  const photos = talk.photos || [];
  if (photos.length > 0) {
    doc.addPage();

    // Page 2 Header
    doc.setFillColor(188, 17, 0);
    doc.rect(0, 0, pageWidth, 3, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text('Anexo de Evidencias Fotográficas', pageWidth / 2, 16, { align: 'center' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.text(`Reporte de Plática de 5 Minutos — ${nombrePlatica} (${formattedDate})`, pageWidth / 2, 22, { align: 'center' });
    doc.text(`Unidad de Negocio: ${unidadNegocio} | Proyecto: ${proyecto} | Exponente: ${exponente}`, pageWidth / 2, 27, { align: 'center' });

    doc.setDrawColor(200, 200, 200);
    doc.line(marginX, 31, pageWidth - marginX, 31);

    // Photos Grid (up to 7 photos)
    const photoCols = photos.length <= 2 ? 1 : (photos.length <= 4 ? 2 : 3);
    const photoSpacing = 5;
    const pBoxW = (contentWidth - ((photoCols - 1) * photoSpacing)) / photoCols;
    const pBoxH = photoCols === 1 ? 75 : (photoCols === 2 ? 65 : 55);

    let col = 0;
    let rowY = 36;

    for (let i = 0; i < Math.min(photos.length, 7); i++) {
      const p = photos[i];
      const pX = marginX + (col * (pBoxW + photoSpacing));

      // Photo frame
      doc.setDrawColor(180, 180, 180);
      doc.setLineWidth(0.3);
      doc.rect(pX, rowY, pBoxW, pBoxH);

      try {
        if (p.dataUrl) {
          doc.addImage(p.dataUrl, 'JPEG', pX + 1.5, rowY + 1.5, pBoxW - 3, pBoxH - 12);
        }
      } catch (err) {
        doc.setFillColor(245, 245, 245);
        doc.rect(pX + 1.5, rowY + 1.5, pBoxW - 3, pBoxH - 12, 'F');
        doc.setFontSize(7.5);
        doc.setTextColor(100, 100, 100);
        doc.text('Fotografía adjunta', pX + (pBoxW / 2), rowY + (pBoxH / 2) - 4, { align: 'center' });
      }

      // Caption banner
      doc.setFillColor(245, 245, 245);
      doc.rect(pX, rowY + pBoxH - 9, pBoxW, 9, 'F');
      doc.setDrawColor(180, 180, 180);
      doc.line(pX, rowY + pBoxH - 9, pX + pBoxW, rowY + pBoxH - 9);

      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.5);
      doc.text(`Foto ${i + 1}: ${p.caption || p.name || 'Evidencia de sesión'}`, pX + 2, rowY + pBoxH - 5);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(5.5);
      doc.text(p.uploadedAt || formattedDate, pX + 2, rowY + pBoxH - 2);

      col++;
      if (col >= photoCols && i < photos.length - 1) {
        col = 0;
        rowY += pBoxH + photoSpacing;
      }
    }

    // Page 2 Footer
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.setTextColor(100, 100, 100);
    doc.text(`Servicios Nuevos De Filtración Hispanomexicános — ISO 9001 / NOM-019-STPS`, marginX, pageHeight - 6);
    doc.text('Página 2 de 2', pageWidth - marginX - 16, pageHeight - 6);
  }

  const cleanTopic = nombrePlatica.replace(/[^a-zA-Z0-9-_]/g, '_').slice(0, 25);
  const cleanDate = (talk.date || '2026-04-29').replace(/[^a-zA-Z0-9-_]/g, '_');
  doc.save(`Reporte_Platica_5_Minutos_${cleanTopic}_${cleanDate}.pdf`);
}

/**
 * Generates the Monthly Safety Talks Planning & Compliance Report (Programa Mensual de Pláticas de 5 Minutos con Cruz de Pláticas)
 */
export async function generateMonthlySafetyTalksPlanPDF(
  monthIndex: number,
  year: number,
  talks: SafetyTalk[],
  costCenterName: string
): Promise<void> {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'letter'
  });

  const pageWidth = 216;
  const pageHeight = 279;
  const marginX = 14;
  const contentWidth = pageWidth - (marginX * 2);

  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];
  const monthName = monthNames[monthIndex];

  // Header
  doc.setFillColor(188, 17, 0);
  doc.rect(0, 0, pageWidth, 3.5, 'F');

  doc.setFillColor(15, 23, 42);
  doc.rect(0, 3.5, pageWidth, 32, 'F');

  try {
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(marginX, 6.5, 26, 26, 2, 2, 'F');
    doc.addImage(CORPORATE_LOGO_BASE64, 'JPEG', marginX + 1, 7.5, 24, 24);
  } catch {
    doc.setFillColor(188, 17, 0);
    doc.roundedRect(marginX, 7.5, 22, 23, 2, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('SNF', marginX + 4, 20);
  }

  const textStartX = marginX + 29;
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12.5);
  doc.text('SERVICIOS NUEVOS DE FILTRACIÓN', textStartX, 13);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(203, 213, 225);
  doc.text('SISTEMA DE GESTIÓN DE SEGURIDAD Y SALUD OCUPACIONAL (ISO 45001:2018)', textStartX, 17.5);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(56, 189, 248);
  doc.text(`PROGRAMA Y CUMPLIMIENTO MENSUAL DE PLÁTICAS DE 5 MINUTOS — ${monthName.toUpperCase()} ${year}`, textStartX, 23.5);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.setTextColor(148, 163, 184);
  doc.text(`Sede Operativa: ${costCenterName || 'Planta Operativa'} | NOM-019-STPS Comisión Mixta de Seguridad e Higiene`, textStartX, 28);

  let currentY = 40;

  // Monthly Metrics Calculation
  const totalTalks = talks.length;
  const appliedCount = talks.filter(t => t.status === 'aplicada' || t.status === 'completada').length;
  const scheduledCount = talks.filter(t => t.status === 'programada').length;
  const pendingCount = talks.filter(t => t.status === 'no_realizada').length;
  const totalAttendees = talks.reduce((acc, t) => acc + (Number(t.attendeesCount) || 0), 0);
  const complianceRate = totalTalks > 0 ? Math.round((appliedCount / totalTalks) * 100) : 0;

  // Metric Cards Row
  const mBoxW = (contentWidth - 12) / 4;
  const metricsList = [
    { title: 'PLÁTICAS TOTALES', val: `${totalTalks}`, sub: 'Registradas', bg: [241, 245, 249], border: [203, 213, 225], text: [15, 23, 42] },
    { title: 'APLICADAS / IMPARTIDAS', val: `${appliedCount}`, sub: `${complianceRate}% Cumplimiento`, bg: [236, 253, 245], border: [16, 185, 129], text: [4, 120, 87] },
    { title: 'PROGRAMADAS', val: `${scheduledCount}`, sub: 'En Calendario', bg: [239, 246, 255], border: [59, 130, 246], text: [29, 78, 216] },
    { title: 'ASISTENCIA TOTAL', val: `${totalAttendees}`, sub: 'Participaciones', bg: [254, 243, 199], border: [245, 158, 11], text: [180, 83, 9] }
  ];

  metricsList.forEach((m, idx) => {
    const bX = marginX + (idx * (mBoxW + 4));
    doc.setFillColor(m.bg[0], m.bg[1], m.bg[2]);
    doc.setDrawColor(m.border[0], m.border[1], m.border[2]);
    doc.setLineWidth(0.3);
    doc.roundedRect(bX, currentY, mBoxW, 14, 1, 1, 'FD');

    doc.setTextColor(100, 116, 139);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(5.5);
    doc.text(m.title, bX + 3, currentY + 3.8);

    doc.setTextColor(m.text[0], m.text[1], m.text[2]);
    doc.setFontSize(9);
    doc.text(m.val, bX + 3, currentY + 8.8);

    doc.setTextColor(100, 116, 139);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(5);
    doc.text(m.sub, bX + 3, currentY + 12);
  });

  currentY += 19;

  // Helper section
  const drawSectionHeader = (title: string, yPos: number): number => {
    doc.setFillColor(241, 245, 249);
    doc.setDrawColor(203, 213, 225);
    doc.setLineWidth(0.3);
    doc.roundedRect(marginX, yPos, contentWidth, 5.5, 1, 1, 'FD');

    doc.setFillColor(188, 17, 0);
    doc.rect(marginX, yPos, 2.5, 5.5, 'F');

    doc.setTextColor(15, 23, 42);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.text(title.toUpperCase(), marginX + 5, yPos + 3.9);

    return yPos + 7.5;
  };

  // Table of talks
  currentY = drawSectionHeader(`Calendario y Registro de Pláticas — ${monthName} ${year}`, currentY);

  // Table header
  doc.setFillColor(15, 23, 42);
  doc.rect(marginX, currentY, contentWidth, 6, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.5);
  doc.text('FECHA', marginX + 3, currentY + 4.2);
  doc.text('FOLIO', marginX + 22, currentY + 4.2);
  doc.text('TEMA DE SEGURIDAD', marginX + 42, currentY + 4.2);
  doc.text('FACILITADOR', marginX + 105, currentY + 4.2);
  doc.text('ÁREA', marginX + 140, currentY + 4.2);
  doc.text('ESTATUS', marginX + 168, currentY + 4.2);

  currentY += 6;

  if (talks.length === 0) {
    doc.setFillColor(248, 250, 252);
    doc.rect(marginX, currentY, contentWidth, 12, 'F');
    doc.setTextColor(148, 163, 184);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(7);
    doc.text('No hay pláticas programadas o registradas en este período mensual.', marginX + (contentWidth / 2), currentY + 7, { align: 'center' });
    currentY += 15;
  } else {
    talks.forEach((t, index) => {
      if (currentY > pageHeight - 35) {
        doc.addPage();
        currentY = 20;
      }

      const isEven = index % 2 === 0;
      doc.setFillColor(isEven ? 255 : 248, isEven ? 255 : 250, isEven ? 255 : 252);
      doc.rect(marginX, currentY, contentWidth, 6.5, 'F');
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.2);
      doc.line(marginX, currentY + 6.5, marginX + contentWidth, currentY + 6.5);

      doc.setTextColor(15, 23, 42);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.5);
      doc.text(t.date || '', marginX + 3, currentY + 4.5);

      doc.setTextColor(188, 17, 0);
      doc.text(t.id || '', marginX + 22, currentY + 4.5);

      doc.setTextColor(15, 23, 42);
      doc.setFont('helvetica', 'normal');
      doc.text((t.topic || '').slice(0, 36), marginX + 42, currentY + 4.5);

      doc.setTextColor(71, 85, 105);
      doc.text((t.facilitator || '').slice(0, 22), marginX + 105, currentY + 4.5);
      doc.text((t.area || '').slice(0, 16), marginX + 140, currentY + 4.5);

      // Status text
      if (t.status === 'aplicada' || t.status === 'completada') {
        doc.setTextColor(4, 120, 87);
        doc.setFont('helvetica', 'bold');
        doc.text(`Aplicada (${t.attendeesCount}p)`, marginX + 168, currentY + 4.5);
      } else if (t.status === 'programada') {
        doc.setTextColor(29, 78, 216);
        doc.setFont('helvetica', 'bold');
        doc.text('Programada', marginX + 168, currentY + 4.5);
      } else {
        doc.setTextColor(180, 83, 9);
        doc.setFont('helvetica', 'bold');
        doc.text('Pendiente', marginX + 168, currentY + 4.5);
      }

      currentY += 6.5;
    });

    currentY += 6;
  }

  // Validation signatures
  if (currentY > pageHeight - 35) {
    doc.addPage();
    currentY = 20;
  }

  const sigWidth = (contentWidth - 6) / 3;
  const sigY = pageHeight - 22;

  const signatures = [
    { title: 'COORDINADOR HSEQ', name: 'Supervisión de Seguridad' },
    { title: 'SECRETARIO COMISIÓN MIXTA', name: 'Representante NOM-019' },
    { title: 'GERENCIA DE PLANTA', name: 'Dirección Operativa' }
  ];

  signatures.forEach((sig, idx) => {
    const sX = marginX + (idx * (sigWidth + 3));
    doc.setDrawColor(100, 116, 139);
    doc.setLineWidth(0.4);
    doc.line(sX, sigY, sX + sigWidth, sigY);

    doc.setTextColor(15, 23, 42);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.5);
    doc.text(sig.title, sX, sigY + 3.2);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(5.5);
    doc.setTextColor(71, 85, 105);
    doc.text(sig.name, sX, sigY + 6.5);
  });

  doc.setFillColor(188, 17, 0);
  doc.rect(0, pageHeight - 3, pageWidth, 3, 'F');

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(5.8);
  doc.setTextColor(148, 163, 184);
  doc.text(`Programa Mensual de Pláticas de Seguridad — ${monthName} ${year} | NOM-019-STPS.`, marginX, pageHeight - 5);
  doc.text('Página 1 de 1', pageWidth - marginX - 16, pageHeight - 5);

  const cleanSede = (costCenterName || 'Sede').replace(/[^a-zA-Z0-9-_]/g, '_');
  doc.save(`Programa_Mensual_Platicas_5Min_${cleanSede}_${year}_${String(monthIndex + 1).padStart(2, '0')}.pdf`);
}

/**
 * Generates an official STPS DC-3 Format (Constancia de Competencias o de Habilidades Laborales)
 * formatted strictly according to Mexican STPS / SIRCE regulatory standards.
 */
export async function generateOfficialDC3PDF(
  dc3: DC3Record,
  companyInfo?: { name?: string; rfc?: string; activity?: string; location?: string }
): Promise<void> {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'letter' // 216 x 279 mm
  });

  const pageWidth = 216;
  const pageHeight = 279;
  const marginX = 14;
  const contentWidth = pageWidth - (marginX * 2); // 188mm

  const companyName = companyInfo?.name || 'SERVICIOS NUEVOS DE FILTRACIÓN S.A. DE C.V.';
  const companyRfc = companyInfo?.rfc || 'SNF120418-XYZ';
  const companyLocation = companyInfo?.location || dc3.costCenterName || 'Planta Operativa Bajío - Salamanca, Gto.';
  const companyActivity = companyInfo?.activity || 'Fabricación, Ensamble y Mantenimiento de Sistemas de Filtración Industrial';

  // 1. Top Decorative Bar
  doc.setFillColor(188, 17, 0); // Brand Red
  doc.rect(0, 0, pageWidth, 4, 'F');

  // 2. Header Container
  doc.setFillColor(255, 255, 255);
  doc.rect(marginX, 7, contentWidth, 26, 'F');
  doc.setDrawColor(30, 41, 59);
  doc.setLineWidth(0.6);
  doc.rect(marginX, 7, contentWidth, 26);

  // Logo
  try {
    doc.addImage(CORPORATE_LOGO_BASE64, 'JPEG', marginX + 3, 9, 22, 22);
  } catch {
    doc.setFillColor(188, 17, 0);
    doc.roundedRect(marginX + 3, 9, 22, 22, 2, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text('SNF', marginX + 7, 21);
  }

  // Header Titles
  doc.setTextColor(15, 23, 42);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('FORMATO DC-3', marginX + 28, 14);

  doc.setFontSize(9.5);
  doc.setTextColor(188, 17, 0);
  doc.text('CONSTANCIA DE COMPETENCIAS O DE HABILIDADES LABORALES', marginX + 28, 19.5);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.setTextColor(71, 85, 105);
  doc.text('SECRETARÍA DEL TRABAJO Y PREVISIÓN SOCIAL (STPS) — SISTEMA SIRCE', marginX + 28, 24);
  doc.text('Conforme a los Artículos 153-A y 153-F de la Ley Federal del Trabajo', marginX + 28, 28);

  // Folio Tag on Top Right
  doc.setFillColor(241, 245, 249);
  doc.rect(pageWidth - marginX - 44, 9, 41, 22, 'F');
  doc.setDrawColor(203, 213, 225);
  doc.setLineWidth(0.3);
  doc.rect(pageWidth - marginX - 44, 9, 41, 22);

  doc.setTextColor(100, 116, 139);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(5.5);
  doc.text('FOLIO DE REGISTRO:', pageWidth - marginX - 42, 13.5);
  doc.setTextColor(188, 17, 0);
  doc.setFontSize(7.5);
  doc.text(dc3.id || 'DC3-2026-001', pageWidth - marginX - 42, 18);

  doc.setTextColor(71, 85, 105);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(5.5);
  doc.text(`Emisión: ${dc3.issueDate || '2026-04-29'}`, pageWidth - marginX - 42, 23);
  doc.text(`Vigencia: ${dc3.expiryDate || '2027-04-29'} (1 Año)`, pageWidth - marginX - 42, 27.5);

  let currentY = 36;

  const drawFieldBox = (label: string, value: string, x: number, y: number, w: number, h: number = 10, isBold = true) => {
    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(203, 213, 225);
    doc.setLineWidth(0.3);
    doc.roundedRect(x, y, w, h, 1, 1, 'FD');

    doc.setTextColor(100, 116, 139);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(5.5);
    doc.text(label.toUpperCase(), x + 2.5, y + 3.5);

    doc.setTextColor(15, 23, 42);
    doc.setFont('helvetica', isBold ? 'bold' : 'normal');
    doc.setFontSize(7);
    const textLines = doc.splitTextToSize(value || 'N/A', w - 5);
    doc.text(textLines, x + 2.5, y + 7.5);
  };

  const drawSectionBanner = (title: string, yPos: number): number => {
    doc.setFillColor(15, 23, 42);
    doc.rect(marginX, yPos, contentWidth, 5.5, 'F');
    doc.setFillColor(188, 17, 0);
    doc.rect(marginX, yPos, 3, 5.5, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.text(title.toUpperCase(), marginX + 6, yPos + 3.9);
    return yPos + 7.5;
  };

  // --- SECCIÓN I: DATOS DEL TRABAJADOR ---
  currentY = drawSectionBanner('I. Datos del Trabajador', currentY);

  drawFieldBox('Nombre Completo (Apellido Paterno, Materno y Nombre(s))', dc3.employeeName || '', marginX, currentY, contentWidth, 11, true);
  currentY += 13;

  const halfW = (contentWidth - 3) / 2;
  const thirdW = (contentWidth - 6) / 3;

  drawFieldBox('Clave Única de Registro de Población (CURP)', dc3.curp || 'CURP NO REGISTRADA', marginX, currentY, halfW, 10, true);
  drawFieldBox('No. de Nómina / ID Empleado', dc3.employeeId || 'ISN-000', marginX + halfW + 3, currentY, halfW, 10, true);
  currentY += 12;

  drawFieldBox('Ocupación Específica / Puesto en la Empresa', dc3.role || 'Operador Técnico de Producción', marginX, currentY, halfW + 20, 10, true);
  drawFieldBox('Clave de Ocupación (Catálogo Nacional STPS)', dc3.occupationCode || '01.1 - Operación Industrial', marginX + halfW + 23, currentY, halfW - 23, 10, false);
  currentY += 14;

  // --- SECCIÓN II: DATOS DE LA EMPRESA ---
  currentY = drawSectionBanner('II. Datos de la Empresa', currentY);

  drawFieldBox('Nombre o Razón Social', companyName, marginX, currentY, halfW + 25, 10, true);
  drawFieldBox('Registro Federal de Contribuyentes (R.F.C.)', companyRfc, marginX + halfW + 28, currentY, halfW - 28, 10, true);
  currentY += 12;

  drawFieldBox('Centro de Trabajo / Sede Operativa', companyLocation, marginX, currentY, halfW + 10, 10, false);
  drawFieldBox('Actividad o Giro Principal', companyActivity, marginX + halfW + 13, currentY, halfW - 13, 10, false);
  currentY += 14;

  // --- SECCIÓN III: DATOS DEL PROGRAMA DE CAPACITACIÓN ---
  currentY = drawSectionBanner('III. Datos del Programa de Capacitación, Adiestramiento y Productividad', currentY);

  drawFieldBox('Nombre del Curso / Evento de Capacitación Acreditado', dc3.courseName || '', marginX, currentY, contentWidth, 11, true);
  currentY += 13;

  drawFieldBox('Duración en Horas', `${dc3.durationHours || 16} Horas Totales`, marginX, currentY, thirdW, 10, true);
  
  const pInicio = dc3.startDate || dc3.issueDate || '2026-04-20';
  const pFin = dc3.endDate || dc3.issueDate || '2026-04-29';
  drawFieldBox('Periodo de Ejecución', `Del ${pInicio} al ${pFin}`, marginX + thirdW + 3, currentY, thirdW, 10, false);
  
  drawFieldBox('Área Temática del Curso (Catálogo STPS)', dc3.thematicArea || '6000 - Seguridad en el Trabajo', marginX + (thirdW * 2) + 6, currentY, thirdW, 10, false);
  currentY += 12;

  drawFieldBox('Nombre del Agente Capacitador / Instructor / Tutor', dc3.trainer || 'Ing. Alondra Tovar (Supervisor HSEQ)', marginX, currentY, halfW + 15, 10, true);
  drawFieldBox('No. de Registro como Agente Capacitador Externo ante STPS', dc3.stpsReg || 'STPS-AUT-2026-ISN09', marginX + halfW + 18, currentY, halfW - 18, 10, true);
  currentY += 12;

  drawFieldBox('Resultado de Evaluación / Calificación', `APROBADO (${dc3.score || '100%'} de Aprovechamiento Teórico-Práctico)`, marginX, currentY, halfW, 10, true);
  drawFieldBox('Tipo de Capacitación', 'Capacitación y Adiestramiento para el Trabajo (Interno / Externo)', marginX + halfW + 3, currentY, halfW, 10, false);
  currentY += 15;

  // --- SECCIÓN IV: FIRMAS Y VALIDACIÓN NORMATIVA ---
  currentY = drawSectionBanner('IV. Firmas de Autenticidad y Validación Normativa (Ley Federal del Trabajo)', currentY);

  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(203, 213, 225);
  doc.setLineWidth(0.3);
  doc.roundedRect(marginX, currentY, contentWidth, 8, 1, 1, 'FD');
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(5.5);
  doc.setTextColor(71, 85, 105);
  doc.text(
    'Los firmantes hacen constar que el trabajador acreditó satisfactoriamente el curso mencionado, conforme a los planes y programas de capacitación registrados ante la Secretaría del Trabajo y Previsión Social.',
    marginX + 3,
    currentY + 3.2,
    { maxWidth: contentWidth - 6 }
  );

  currentY += 11;

  // 3 Signature Boxes
  const sigColW = (contentWidth - 6) / 3;
  const sigHeight = 27;

  // 1. Instructor
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(203, 213, 225);
  doc.roundedRect(marginX, currentY, sigColW, sigHeight, 1, 1, 'FD');
  doc.setDrawColor(100, 116, 139);
  doc.line(marginX + 5, currentY + 18, marginX + sigColW - 5, currentY + 18);
  doc.setTextColor(15, 23, 42);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.5);
  doc.text((dc3.trainer || 'Instructor / Tutor').slice(0, 26), marginX + (sigColW / 2), currentY + 21, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(5.5);
  doc.setTextColor(100, 116, 139);
  doc.text('Instructor o Agente Capacitador', marginX + (sigColW / 2), currentY + 24.5, { align: 'center' });

  // 2. Patrón
  const s2X = marginX + sigColW + 3;
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(203, 213, 225);
  doc.roundedRect(s2X, currentY, sigColW, sigHeight, 1, 1, 'FD');
  doc.setDrawColor(100, 116, 139);
  doc.line(s2X + 5, currentY + 18, s2X + sigColW - 5, currentY + 18);
  doc.setTextColor(15, 23, 42);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.5);
  doc.text((dc3.legalRepresentativeName || 'Ing. Fernando Guzmán V.').slice(0, 26), s2X + (sigColW / 2), currentY + 21, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(5.5);
  doc.setTextColor(100, 116, 139);
  doc.text('Patrón o Representante Legal', s2X + (sigColW / 2), currentY + 24.5, { align: 'center' });

  // 3. Representante Trabajadores
  const s3X = marginX + (sigColW * 2) + 6;
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(203, 213, 225);
  doc.roundedRect(s3X, currentY, sigColW, sigHeight, 1, 1, 'FD');
  doc.setDrawColor(100, 116, 139);
  doc.line(s3X + 5, currentY + 18, s3X + sigColW - 5, currentY + 18);
  doc.setTextColor(15, 23, 42);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.5);
  doc.text((dc3.workerRepresentativeName || 'Comisión Mixta NOM-019').slice(0, 26), s3X + (sigColW / 2), currentY + 21, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(5.5);
  doc.setTextColor(100, 116, 139);
  doc.text('Representante de los Trabajadores', s3X + (sigColW / 2), currentY + 24.5, { align: 'center' });

  currentY += sigHeight + 3;

  // Footer bar and compliance notices
  doc.setFillColor(188, 17, 0);
  doc.rect(0, pageHeight - 4, pageWidth, 4, 'F');

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(5.5);
  doc.setTextColor(100, 116, 139);
  doc.text(
    `VIGENCIA: 1 AÑO A PARTIR DE SU FECHA DE EMISIÓN (${dc3.issueDate || '2026-04-29'}) | VENCE: ${dc3.expiryDate || '2027-04-29'}. Este documento certifica la aprobación del curso de habilidades y destrezas conforme a la normativa STPS vigente.`,
    marginX,
    pageHeight - 6
  );
  doc.text('Página 1 de 1', pageWidth - marginX - 14, pageHeight - 6);

  // Save PDF
  const cleanName = (dc3.employeeName || 'Colaborador').replace(/[^a-zA-Z0-9-_]/g, '_').slice(0, 25);
  const cleanCourse = (dc3.courseName || 'Curso').replace(/[^a-zA-Z0-9-_]/g, '_').slice(0, 20);
  doc.save(`Formato_DC3_STPS_${cleanName}_${cleanCourse}.pdf`);
}

/**
 * Generates an official summary PDF of all DC-3 certificates inside an employee's digital folder
 */
export async function generateDC3FolderSummaryPDF(
  employeeName: string,
  records: DC3Record[],
  costCenterName?: string
): Promise<void> {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'letter'
  });

  const pageWidth = 216;
  const pageHeight = 279;
  const marginX = 14;
  const contentWidth = pageWidth - (marginX * 2);

  // Header top
  doc.setFillColor(188, 17, 0);
  doc.rect(0, 0, pageWidth, 3.5, 'F');

  doc.setFillColor(15, 23, 42);
  doc.rect(0, 3.5, pageWidth, 30, 'F');

  try {
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(marginX, 6.5, 24, 24, 2, 2, 'F');
    doc.addImage(CORPORATE_LOGO_BASE64, 'JPEG', marginX + 1, 7.5, 22, 22);
  } catch {
    doc.setFillColor(188, 17, 0);
    doc.roundedRect(marginX, 7.5, 22, 22, 2, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text('SNF', marginX + 4, 19);
  }

  const textStartX = marginX + 27;
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('SERVICIOS NUEVOS DE FILTRACIÓN S.A. DE C.V.', textStartX, 12);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(203, 213, 225);
  doc.text('EXPEDIENTE DIGITAL DE COMPETENCIAS Y CONSTANCIAS DC-3 (STPS / SIRCE)', textStartX, 16.5);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(52, 211, 153);
  doc.text(`KÁRDEX NORMATIVO: ${employeeName.toUpperCase()}`, textStartX, 22.5);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.setTextColor(148, 163, 184);
  doc.text(`Sede Operativa: ${costCenterName || 'Planta Operativa'} | ${records.length} Formatos DC-3 Registrados`, textStartX, 27.5);

  let currentY = 38;

  // Table header
  doc.setFillColor(15, 23, 42);
  doc.rect(marginX, currentY, contentWidth, 6, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.5);
  doc.text('FOLIO', marginX + 3, currentY + 4.2);
  doc.text('CURSO / NORMA STPS', marginX + 26, currentY + 4.2);
  doc.text('CAPACITADOR', marginX + 98, currentY + 4.2);
  doc.text('EMISIÓN', marginX + 138, currentY + 4.2);
  doc.text('VIGENCIA (1 AÑO)', marginX + 158, currentY + 4.2);

  currentY += 6;

  records.forEach((rec, idx) => {
    const isEven = idx % 2 === 0;
    doc.setFillColor(isEven ? 255 : 248, isEven ? 255 : 250, isEven ? 255 : 252);
    doc.rect(marginX, currentY, contentWidth, 7, 'F');
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.2);
    doc.line(marginX, currentY + 7, marginX + contentWidth, currentY + 7);

    doc.setTextColor(188, 17, 0);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.5);
    doc.text(rec.id, marginX + 3, currentY + 4.8);

    doc.setTextColor(15, 23, 42);
    doc.setFont('helvetica', 'normal');
    doc.text(rec.courseName.slice(0, 44), marginX + 26, currentY + 4.8);

    doc.setTextColor(71, 85, 105);
    doc.text((rec.trainer || 'Instructor HSEQ').slice(0, 24), marginX + 98, currentY + 4.8);

    doc.text(rec.issueDate || '', marginX + 138, currentY + 4.8);

    doc.setFont('helvetica', 'bold');
    if (rec.status === 'vigente') {
      doc.setTextColor(4, 120, 87);
      doc.text(`${rec.expiryDate} (Vigente)`, marginX + 158, currentY + 4.8);
    } else if (rec.status === 'por_renovar') {
      doc.setTextColor(217, 119, 6);
      doc.text(`${rec.expiryDate} (Por Vencer)`, marginX + 158, currentY + 4.8);
    } else {
      doc.setTextColor(220, 38, 38);
      doc.text(`${rec.expiryDate} (Vencido)`, marginX + 158, currentY + 4.8);
    }

    currentY += 7;
  });

  // Footer
  doc.setFillColor(188, 17, 0);
  doc.rect(0, pageHeight - 4, pageWidth, 4, 'F');
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6);
  doc.setTextColor(100, 116, 139);
  doc.text(`Expediente Digital de Capacitación STPS — ${employeeName}`, marginX, pageHeight - 6);
  doc.text('Página 1 de 1', pageWidth - marginX - 14, pageHeight - 6);

  const clean = employeeName.replace(/[^a-zA-Z0-9-_]/g, '_');
  doc.save(`Expediente_DC3_${clean}.pdf`);
}
